import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { ApiError, CardView, useAuth } from '@forbiz/web-kit'
import {
  createOrderRequest,
  type CardFrame,
  type CardTextPosition,
  type CatalogItem,
  type OccasionType,
  type ParsedLinkDto,
  type SavedAddress,
  type FuneralHallRoom,
  type FuneralHallSummary,
  type WreathDesignId,
  wreathDesignId,
} from '@forbiz/contracts'

import { BottomSheet } from '../components/BottomSheet.js'
import { DatePickerSheet } from '../components/DatePickerSheet.js'
import { ProductInfo } from '../components/ProductInfo.js'
import { RibbonPreview } from '../components/RibbonPreview.js'
import { Chrysanthemum, WreathDesignPicker } from '../components/WreathDesign.js'
import { DOW, parseLocalDate } from '../lib/calendar.js'
import { cardSkinsApi } from '../lib/cardSkins.js'
import { digitalWreathApi, getWreathDesign, type WreathDesign } from '../lib/digitalWreath.js'
import { funeralHallApi } from '../lib/funeralHall.js'
import { companyPartnersApi } from '../lib/companyPartners.js'
import { openPostcodeSearch } from '../lib/daumPostcode.js'
import { obituaryApi } from '../lib/obituary.js'
import { companyPhrasesApi } from '../lib/companyPhrases.js'
import { getFavorites } from '../lib/favorites.js'
import { buildOrderBody, EMPTY_FORM, orderToForm, type FormState } from '../lib/orderForm.js'
import { myCompanyApi } from '../lib/myCompany.js'
import { ordersApi } from '../lib/orders.js'
import { paymentsApi } from '../lib/payments.js'
import { pointPrice } from '../lib/pointPrice.js'
import { pointsApi } from '../lib/points.js'
import { defaultProductImage } from '../lib/productImage.js'
import { SYSTEM_PHRASES, ribbonPhrasePlaceholder, senderNamePlaceholder } from '../lib/phrases.js'
import { senderPresetsApi } from '../lib/senderPresets.js'
import { isTossUserCancel, startTossPayment } from '../lib/toss.js'
import { useAsync } from '../lib/useAsync.js'
import s from './OrderCreatePage.module.css'

/**
 * 기업 신규 주문서 (BIZ-9). BIZ-6 계약(createOrderRequest) 위에 있다.
 *
 * 3단계 위저드: ① 유형·상품 → ② 배송·행사 정보 → ③ 문구·확인.
 * 단계는 화면 분할일 뿐이고, 접수/임시저장 시 계약(superRefine 포함)이 payload 전체를
 * 한 번에 검증한다 — 단계별 부분검증을 따로 두지 않아 규칙이 한 곳에 남는다.
 *
 * 배송지는 도로명주소 검색(다음 우편번호 위젯)·최근 배송지로 채우되, 자유 입력도 유지한다
 * (DB에 없는 장례식장도 그대로 타이핑 가능 — 검색은 보조일 뿐). BIZ-29.
 * 범위 메모: 발신자 프리셋 선택은 후속.
 */

/** 유형 세그먼트(짧은 라벨). 상세 설명은 상품 카드가 대신한다. */
const OCCASION_SEG: { value: OccasionType; label: string }[] = [
  { value: 'FUNERAL', label: '근조' },
  { value: 'CELEBRATION', label: '축하' },
  { value: 'GIFT', label: '난·기타' },
]

const OCCASION_LABEL: Record<OccasionType, string> = { FUNERAL: '근조', CELEBRATION: '축하', GIFT: '난·기타' }

/** 'YYYY-MM-DD' → '10월 2일 (목)'. 희망 배송일 표시용. */
function fmtDateKo(v: string): string {
  const d = parseLocalDate(v)
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${DOW[d.getDay()]})`
}

const TIME_SLOTS: { value: string; label: string }[] = [
  { value: '', label: '시간 무관' },
  { value: 'BEFORE_12', label: '오전 중 (12시 전)' },
  { value: 'BEFORE_14', label: '14시 전' },
  { value: 'BEFORE_16', label: '16시 전' },
  { value: 'ANYTIME', label: '아무 때나' },
]

const STEPS = ['유형·상품', '배송·행사 정보', '문구·카드', '결제·확인'] as const

/** 단계별 큰 친근한 제목·부제(사람 말). 목업 언어 — "리본은 이렇게 나가요" 톤. */
const STEP_HEADS: { title: string; lead: string }[] = [
  { title: '무엇을 보낼까요?', lead: '받는 상황에 맞춰 화환을 골라 주세요.' },
  { title: '어디로 보낼까요?', lead: '받는 곳과 받는 분을 알려 주세요.' },
  { title: '문구를 담아 볼까요?', lead: '리본에 나갈 문구와 카드를 정해요.' },
  { title: '이대로 보낼까요?', lead: '주문 내용과 결제를 확인해요.' },
]

/** 필드 → 단계. 접수 검증 실패 시 그 필드가 있는 가장 앞 단계로 이동해 에러를 보여준다. */
const STEP_OF: Record<string, number> = {
  occasionType: 1, productId: 1, quantity: 1, desiredDate: 2,
  deliveryAddress: 2, deliveryAddressDetail: 2, deliveryPlaceName: 2, deliveryZipcode: 2,
  desiredTimeSlot: 2, recipientPhone: 2, deliveryNote: 2,
  mortuaryRoom: 2, deceasedName: 2, mournerName: 2,
  eventType: 2, eventName: 2, eventTarget: 2,
  recipientName: 2, recipientOrg: 2, recipientDept: 2, recipientTitle: 2,
  partnerName: 2,
  ribbonLine1: 3, ribbonLine2: 3, cardMessage: 3, digitalCardMessage: 3, senderName: 3, companyMemo: 3,
}

/** 에러 키들이 걸친 가장 앞 단계. 없으면 현재 단계 유지. */
function earliestErrorStep(keys: string[], fallback: number): number {
  const steps = keys.map((k) => STEP_OF[k]).filter((n): n is number => n != null)
  return steps.length ? Math.min(...steps) : fallback
}

/**
 * 검증 실패 시 첫 에러 필드로 스크롤·포커스. 긴 단계에서 에러가 화면 밖에 뜨면
 * "접수를 눌렀는데 아무 반응이 없다"가 되는 걸 막는다(예: 3단계 리본 문구 누락).
 * 단계 전환·에러 렌더가 끝난 뒤 잡아야 해서 다음 틱으로 미룬다. Field가 label로
 * 입력을 감싸므로 .ferr에서 입력을 역추적하고, label 밖 에러(상품 선택 등)는
 * 에러 문구 자체로 스크롤한다.
 */
function focusFirstError() {
  window.setTimeout(() => {
    const err = document.querySelector('.ferr')
    if (!err) return
    const input = err.closest('label')?.querySelector<HTMLElement>('input, textarea, select')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ;(input ?? err).scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' })
    input?.focus({ preventScroll: true })
  }, 60)
}

/** 제출 전 확인 요약 한 줄. cls는 강조(받는 분=top, 금액=total)용. */
interface SumRow {
  key: string
  label: string
  value: string
  sub?: string | undefined
  cls?: 'top' | 'total' | undefined
}

/**
 * 제출 전 확인 요약 행을 만든다 (BIZ-81).
 * 가장 큰 사고는 "다른 고인·다른 곳 발송"이라 받는 분을 맨 위에 두고, 금액은 맨 아래.
 * 순수 함수 — 폼 상태를 읽어 표시행만 만든다(부수효과 없음).
 */
function buildConfirmRows(occasion: OccasionType, form: FormState, product: CatalogItem | null): SumRow[] {
  const dash = '—'
  const rows: SumRow[] = []

  // 받는 분 (유형별로 누구에게 가는지)
  if (occasion === 'FUNERAL') {
    const who = [form.deceasedName && `故 ${form.deceasedName}`, form.mournerName && `${form.mournerName} 상주`]
      .filter(Boolean)
      .join(' · ')
    rows.push({ key: 'to', label: '받는 분', value: who || dash, sub: form.mortuaryRoom.trim() || undefined, cls: 'top' })
  } else if (occasion === 'CELEBRATION') {
    const who = [form.eventTarget, form.eventName].map((s) => s.trim()).filter(Boolean).join(' · ')
    rows.push({ key: 'to', label: '받는 분', value: who || dash, sub: form.eventType.trim() || undefined, cls: 'top' })
  } else {
    const sub = [form.recipientOrg, form.recipientDept, form.recipientTitle].map((s) => s.trim()).filter(Boolean).join(' ')
    rows.push({ key: 'to', label: '받는 분', value: form.recipientName.trim() || dash, sub: sub || undefined, cls: 'top' })
  }

  // 어디로
  const place = form.deliveryPlaceName.trim()
  const addr = [form.deliveryAddress, form.deliveryAddressDetail].map((s) => s.trim()).filter(Boolean).join(' ')
  rows.push({ key: 'where', label: '주소', value: place || addr || dash, sub: place ? addr || undefined : undefined })

  // 언제 (시간대는 특정 슬롯을 골랐을 때만 보조로)
  const timeLabel = form.desiredTimeSlot ? TIME_SLOTS.find((s) => s.value === form.desiredTimeSlot)?.label : undefined
  const when = form.desiredDate
    ? `${form.desiredDate === todayLocal() ? '즉시 · ' : ''}${fmtDateKo(form.desiredDate)}`
    : dash
  rows.push({ key: 'when', label: '배송희망일', value: when, sub: timeLabel })

  // 무엇을
  const qty = Math.max(1, Number(form.quantity) || 1)
  rows.push({ key: 'what', label: '상품', value: product ? `${product.name} · ${qty}개` : dash })

  // 리본 (전 유형 공통 — 난·기타도 리본으로 나감)
  const l1 = form.ribbonLine1.trim()
  const l2 = form.ribbonLine2.trim() || form.senderName.trim()
  rows.push({ key: 'ribbon', label: '리본', value: l1 || dash, sub: l2 || undefined })

  // 금액 (맨 아래·강조)
  if (product) {
    rows.push({ key: 'total', label: '금액', value: `${(product.price * qty).toLocaleString()}원`, cls: 'total' })
  }

  return rows
}

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m5 13 4 4 10-10" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** 로컬 기준 오늘(YYYY-MM-DD). 새 주문의 희망 배송일 기본값. */
function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function OrderCreatePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const companyId = user?.companyId ?? null

  // /orders/:id/edit면 임시저장을 이어서 작성한다(없으면 신규).
  const { id: editIdParam } = useParams<{ id: string }>()
  const editId = editIdParam ? Number(editIdParam) : null
  const location = useLocation()

  const [step, setStep] = useState(1)
  const [occasion, setOccasion] = useState<OccasionType | null>(null)
  const [productId, setProductId] = useState<number | null>(null)
  const [senderPresetId, setSenderPresetId] = useState<number | null>(null)
  const [cardPreviewOpen, setCardPreviewOpen] = useState(false) // 디지털 카드 전체화면 미리보기(모달)
  // 새 주문은 희망 배송일을 오늘로 기본 설정(사용자가 바꿀 수 있음). 이어서 작성·수정은 불러온 값이 이긴다.
  const [form, setForm] = useState<FormState>(() =>
    editId ? EMPTY_FORM : { ...EMPTY_FORM, desiredDate: todayLocal() },
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  // 자동 임시저장(A안): editId면 그 초안, 없으면 첫 자동저장 때 생성된 id를 보관해 이후 갱신·제출에 재사용.
  const [draftId, setDraftId] = useState<number | null>(editId)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  // 유저당 초안 1개(BIZ-80 후속): 새 주문 진입 시 기존 미완성 초안이 있으면 그 슬롯을 재사용해
  // 초안이 난립하지 않게 한다. 이 확인이 끝나기 전엔 자동저장을 미뤄, 새 초안을 중복 생성하지 않는다.
  const [draftChecked, setDraftChecked] = useState(editId != null)

  // 하단 고정 CTA 바는 단계·안내 문구에 따라 높이가 달라진다 — 고정 여백(예전 124px)이면 바가 마지막
  // 필드를 가렸다. 실제 높이를 재서 본문 아래 여백으로 쓴다.
  const footerRef = useRef<HTMLDivElement>(null)
  const [footerH, setFooterH] = useState(0)
  useEffect(() => {
    const el = footerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setFooterH(el.offsetHeight))
    ro.observe(el)
    return () => ro.disconnect()
  }, [companyId])
  // 주문서는 고정 헤더·CTA 바 사이의 앱형 화면이다. 페이지 끝에서 휠·트랙패드를 더 굴리면 브라우저 고무줄
  // 스크롤(overscroll)로 헤더·바까지 통째로 출렁이고, 시트가 스크롤을 잠글 때 스크롤바가 사라져 화면이
  // 좌우로 밀렸다. 이 화면에 있는 동안만 둘 다 막는다.
  useEffect(() => {
    const root = document.documentElement
    const prev = { overscroll: root.style.overscrollBehavior, gutter: root.style.scrollbarGutter }
    root.style.overscrollBehavior = 'none'
    root.style.scrollbarGutter = 'stable'
    return () => {
      root.style.overscrollBehavior = prev.overscroll
      root.style.scrollbarGutter = prev.gutter
    }
  }, [])

  // 부고 URL 파싱(BIZ-79). URL을 붙여넣으면 서버가 파싱해 근조 필드를 미리 채운다.
  const [obituaryUrl, setObituaryUrl] = useState('')
  const linkInputRef = useRef<HTMLInputElement>(null)
  const [obitBusy, setObitBusy] = useState(false)
  const [obitError, setObitError] = useState('')
  const [obitWarnings, setObitWarnings] = useState<string[]>([])
  // 신뢰도 낮아 사용자가 확인해야 할 필드 라벨(예: 희망 배송일)
  const [obitConfirm, setObitConfirm] = useState<string[]>([])
  // 자동 채움 추적(BIZ-128): 파서가 채운 폼 필드 → 채움 주체. 사용자가 고치면 그 키를 지운다.
  const [autoFilled, setAutoFilled] = useState<Record<string, 'parser' | 'ai'>>({})
  // 확신 낮은 상주명은 자동 입력하지 않는다(BIZ-128) — 제안으로만 보여주고 탭하면 채운다.
  const [mournerSuggest, setMournerSuggest] = useState('')
  // 2단계에서 붙인 링크의 유형이 고른 유형과 다를 때(회의 S4) — 바꿀지 묻는 시트에 쓸 파싱 결과.
  const [linkConflict, setLinkConflict] = useState<ParsedLinkDto | null>(null)
  // 희망 배송일 달력 시트(회의 S4).
  const [dateSheetOpen, setDateSheetOpen] = useState(false)

  const catalog = useAsync(
    () => (companyId ? ordersApi.catalog(companyId) : Promise.resolve({ items: [] })),
    [companyId],
  )

  // 발신자 프리셋(기업 스코프). 실패해도 주문서는 그대로 — 직접 입력으로 폴백한다.
  const presetsAsync = useAsync(
    () => (companyId ? senderPresetsApi.list().catch(() => ({ items: [] })) : Promise.resolve({ items: [] })),
    [companyId],
  )
  const presets = presetsAsync.data?.items ?? []

  // 거래처(BIZ-49). 등록된 거래처를 자동완성으로 제공한다. 자유 입력도 허용.
  const partnersAsync = useAsync(
    () => (companyId ? companyPartnersApi.list().catch(() => ({ items: [] })) : Promise.resolve({ items: [] })),
    [companyId],
  )
  const partners = partnersAsync.data?.items ?? []
  // 거래처 제안 드롭다운(커스텀). 입력값 포함 매칭, 비면 전체.
  const [partnerOpen, setPartnerOpen] = useState(false)
  // 2단계 '추가 정보'(상세주소·거래처·연락처·요청사항) 접기. 기본 접힘 — 인풋 벽 완화.
  const [showMore, setShowMore] = useState(false)
  // 접힌 필드라도 값(부고 링크 자동입력 등)이나 에러가 있으면 자동으로 펼쳐 놓친 걸 막는다.
  const moreFieldsFilled =
    !!form['deliveryAddressDetail'] || !!form['partnerName'] || !!form['recipientPhone'] || !!form['deliveryNote']
  const moreFieldsError =
    !!errors['deliveryAddressDetail'] || !!errors['partnerName'] || !!errors['recipientPhone'] || !!errors['deliveryNote']
  const moreOpen = showMore || moreFieldsFilled || moreFieldsError
  // 3단계 디지털 카드(선택 애드온) 접기. 기본 접힘 — 문구 화면을 가볍게.
  const [showCard, setShowCard] = useState(false)
  const partnerSuggests = useMemo(() => {
    const q = form.partnerName.trim().toLowerCase()
    return q ? partners.filter((p) => p.name.toLowerCase().includes(q)) : partners
  }, [partners, form.partnerName])
  // 거래처 원탭 등록(관리자만) — 자유입력을 조용히 자동 등록하면 오타·일회성 이름이
  // 마스터를 오염시키므로, 미등록 이름일 때 드롭다운 마지막에 명시적 '등록' 옵션만 낸다.
  // 담당자는 서버 권한상 등록 불가(자유입력은 그대로 스냅샷·집계됨).
  const partnerTrimmed = form.partnerName.trim()
  const canAddPartner =
    user?.role === 'COMPANY_ADMIN' && partnerTrimmed.length > 0 && !partners.some((p) => p.name === partnerTrimmed)
  const [partnerAddBusy, setPartnerAddBusy] = useState(false)
  async function addPartner() {
    if (!canAddPartner || partnerAddBusy) return
    setPartnerAddBusy(true)
    try {
      await companyPartnersApi.create({ name: partnerTrimmed })
      setForm((f) => ({ ...f, partnerName: partnerTrimmed }))
      setPartnerOpen(false)
    } catch {
      // 중복(방금 다른 데서 등록)이면 재조회로 링크되고, 그 외 실패는 자유입력 그대로 — 접수를 막지 않는다.
    } finally {
      partnersAsync.reload() // 성공·중복 어느 쪽이든 목록을 새로 그려 id 링크를 살린다
      setPartnerAddBusy(false)
    }
  }

  // 회사 자주 쓰는 문구(BIZ-48). 시스템 상용구와 합쳐 유형별 칩으로 노출한다.
  const companyPhrasesAsync = useAsync(
    () => (companyId ? companyPhrasesApi.list().catch(() => ({ items: [] })) : Promise.resolve({ items: [] })),
    [companyId],
  )
  const companyPhrases = companyPhrasesAsync.data?.items ?? []
  const phrasesFor = (occ: OccasionType): string[] => [
    ...SYSTEM_PHRASES[occ],
    ...companyPhrases.filter((c) => c.occasionType === occ).map((c) => c.phrase),
  ]

  // 신규 주문이고 아직 발신자를 안 정했으면 기본 프리셋을 자동 선택한다(반복 주문 1분).
  // 이어서 작성(editId)이면 저장된 senderName을 존중하고 건드리지 않는다.
  useEffect(() => {
    if (editId || senderPresetId != null || form.senderName.trim() !== '') return
    const def = presetsAsync.data?.items.find((p) => p.isDefault)
    if (def) {
      setSenderPresetId(def.id)
      setForm((f) => ({ ...f, senderName: def.senderName }))
    }
    // presets 로드 시 한 번 실행. form은 의도적으로 deps에서 뺀다(자동 선택 후 재실행 방지).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetsAsync.data, editId])

  // 홈 '자주 보낸 곳'에서 넘어오면 배송지를 미리 채운다(BIZ-44). 유형·상품은 그대로 고른다.
  useEffect(() => {
    const pre = (
      location.state as {
        prefillAddress?: { address: string; addressDetail: string | null; placeName: string | null; zipcode: string | null }
      } | null
    )?.prefillAddress
    if (!pre || editIdParam) return
    setForm((f) => ({
      ...f,
      deliveryAddress: pre.address,
      deliveryAddressDetail: pre.addressDetail ?? '',
      deliveryPlaceName: pre.placeName ?? '',
      deliveryZipcode: pre.zipcode ?? '',
    }))
    // 마운트 시 한 번만. location.state는 진입 시 고정이라 deps에 넣지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 홈 히어로(BIZ-99)에서 링크를 붙여넣고 넘어오면 바로 파싱해 유형·필드를 채운다.
  // 범용 파싱 흐름(loadFromLink)을 재사용한다. 이어서 작성(editId)이면 건드리지 않는다.
  useEffect(() => {
    const url = (location.state as { obituaryUrl?: string } | null)?.obituaryUrl
    if (!url || editIdParam) return
    setObituaryUrl(url)
    void loadFromLink(url)
    // 마운트 시 한 번만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 카탈로그(전체 화환)·홈 상품 카드에서 넘어오면 유형·상품을 프리필한 채 연다(상품 선택 스킵).
  useEffect(() => {
    const pre = (
      location.state as { presetProduct?: { occasionType: OccasionType; productId: number } } | null
    )?.presetProduct
    if (!pre || editIdParam) return
    setOccasion(pre.occasionType)
    setProductId(pre.productId)
    // 마운트 시 한 번만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 새 주문은 희망 배송일을 오늘로 기본 설정한다(비어 있을 때만 — 사용자가 바꾼 값·불러온 값은 안 건드림).
  useEffect(() => {
    if (editId) return
    setForm((f) => (f.desiredDate ? f : { ...f, desiredDate: todayLocal() }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 새 주문: 기존 미완성 초안(주문번호 없음)이 있으면 그 슬롯을 재사용한다(A안 — 유저당 초안 1개).
  // 확인이 끝날 때까지 자동저장은 대기(draftChecked)해, 새 초안을 중복 생성하지 않는다.
  useEffect(() => {
    if (editId) return
    let alive = true
    ordersApi
      .list({ limit: 20 })
      .then((res) => {
        if (!alive) return
        const existing = res.items.find((i) => i.orderNo === null)
        if (existing) setDraftId(existing.id)
        setDraftChecked(true)
      })
      .catch(() => {
        if (alive) setDraftChecked(true)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 이어서 작성(임시저장) 또는 접수된 주문 정보 수정(기업 셀프편집·발주 전)을 위해 기존 주문을
  // 불러 폼을 채운다(한 번만). 편집 불가 상태(발주 후·배송 등)면 상세로 돌린다.
  const draft = useAsync(() => (editId ? ordersApi.detail(editId) : Promise.resolve(null)), [editId])

  // 새 주문(/orders/new) 진입 시 작성 중이던 초안이 있으면 안내한다 — 임시저장은 유저당
  // 1슬롯이라, 여기서 새로 저장하면 그 초안을 덮어쓰기 때문(조용한 유실 방지).
  const existingDraft = useAsync(
    () => (!editId && companyId != null ? ordersApi.home() : Promise.resolve(null)),
    [editId, companyId],
  )
  const priorDraft = !editId ? existingDraft.data?.draft ?? null : null
  const [prefilled, setPrefilled] = useState(false)
  // 접수된(주문번호 있는) 주문을 고치는 중이면 amend 모드 — 저장 시 /body(본문 편집)로 보낸다.
  const [amend, setAmend] = useState(false)
  // 발주 후~준비 중이면 텍스트만 수정(상품·유형·수량 잠금). 서버가 canCompanyEditTextOnly로 판정(BIZ-71).
  const [textOnly, setTextOnly] = useState(false)
  useEffect(() => {
    if (!editId || prefilled) return
    const o = draft.data?.order
    if (!o) return
    if (o.orderNo !== null) {
      // 배송 시작 전까지 기업이 직접 수정 가능. 발주 전이면 전체, 발주~준비 중이면 텍스트만. 그 밖(배송 후 등)은 수정 불가.
      if (!o.canCompanyEdit && !o.canCompanyEditTextOnly) {
        navigate(`/orders/${editId}`, { replace: true })
        return
      }
      setAmend(true)
      setTextOnly(!o.canCompanyEdit && Boolean(o.canCompanyEditTextOnly))
    }
    setOccasion(o.occasionType)
    setProductId(o.item?.productId ?? null)
    const loaded = orderToForm(o)
    // 임시저장(주문번호 없음)을 이어 쓸 때, 저장된 희망일이 비었거나 이미 지난 날짜면 오늘로 되돌린다.
    // (과거 날짜는 배송이 불가능한 무효값 — 접수된 주문 수정(amend)은 저장값을 그대로 둔다.)
    if (o.orderNo === null && (!loaded.desiredDate || loaded.desiredDate < todayLocal())) {
      loaded.desiredDate = todayLocal()
    }
    setForm(loaded)
    setHallId(o.funeralHall?.id ?? null) // 장례식장 연결 복원(BIZ-90)
    // 디지털 카드(BIZ-86) 저장값 복원
    setCardSkinId(o.digitalCardSkinId ?? null)
    setCardMsg(o.digitalCardMessage ?? '')
    // 디지털 화환(BIZ-126) 저장값 복원 — 디자인도 회사 설정이 아니라 그 주문에 실린 값으로 그린다.
    // 접수된 주문(amend)은 저장된 켬/끔이 사용자의 선택이라 그대로 둔다. 초안에 화환이 없으면
    // 아직 안 정한 것으로 보고 제휴 식장 기본 선택이 다시 적용되게 둔다.
    const savedWreath = wreathDesignId.safeParse(o.digitalWreathDesign)
    if (savedWreath.success) setPickedWreathDesign(savedWreath.data)
    setDigitalWreathOn(o.digitalWreathDesign != null)
    setWreathTouched(o.orderNo !== null || o.digitalWreathDesign != null)
    setPrefilled(true)
  }, [editId, prefilled, draft.data, navigate])
  // 장례식장 연결 상태(BIZ-90) — 카탈로그 필터(byOccasion)가 먼저 읽어 여기서 선언한다. 로딩·판정은 아래 효과.
  const [hallId, setHallId] = useState<number | null>(null)
  const [linkedHall, setLinkedHall] = useState<FuneralHallSummary | null>(null)
  const [hallRooms, setHallRooms] = useState<FuneralHallRoom[]>([])
  const [hallBlockedIds, setHallBlockedIds] = useState<number[]>([])
  const items = catalog.data?.items ?? []
  const byOccasion = useMemo(
    () =>
      occasion
        ? items.filter(
            (i) =>
              i.occasionType === occasion &&
              // 식장이 먼저 정해졌으면(URL 파싱·이어서 작성) 그 식장이 안 받는 상품은 처음부터 뺀다(BIZ-90 S3).
              !(occasion === 'FUNERAL' && linkedHall != null && (!linkedHall.wreathAccepted || hallBlockedIds.includes(i.productId))),
          )
        : [],
    [items, occasion, linkedHall, hallBlockedIds],
  )
  // 즐겨찾기(기기별 로컬)로 주문 상품을 빠르게 좁힌다 — 있으면 즐겨찾기만 먼저 보여주고 '전체'로 펼친다.
  const favSet = useMemo(() => new Set(getFavorites()), [])
  const favByOccasion = useMemo(() => byOccasion.filter((i) => favSet.has(i.productId)), [byOccasion, favSet])
  const [showAllProducts, setShowAllProducts] = useState(false)
  const shownProducts = useMemo(() => {
    if (showAllProducts || favByOccasion.length === 0) return byOccasion
    // 즐겨찾기 + 지금 선택된(프리필 등) 상품은 항상 포함해 숨지 않게 한다.
    return byOccasion.filter((i) => favSet.has(i.productId) || i.productId === productId)
  }, [showAllProducts, favByOccasion, byOccasion, favSet, productId])
  const selected = items.find((i) => i.productId === productId) ?? null
  // 상품 상세 시트(주문서 안에서 상세 조회 — 페이지를 떠나지 않는다).
  const [detailId, setDetailId] = useState<number | null>(null)
  const detailItem = detailId != null ? items.find((i) => i.productId === detailId) ?? null : null
  const canLeaveStep1 = occasion !== null && productId !== null

  /** 자동 입력 표시 해제(BIZ-128) — 사용자가 직접 값을 바꾼 필드는 배지를 거둔다. */
  const clearAuto = (...keys: string[]) =>
    setAutoFilled((a) => {
      const next = { ...a }
      let changed = false
      for (const k of keys)
        if (k in next) {
          delete next[k]
          changed = true
        }
      return changed ? next : a
    })

  // 희망 배송일(회의 S4): '즉시' = 오늘. 날짜를 직접 고르면 파서 자동 입력 배지와 에러를 거둔다.
  const isTodayDate = form['desiredDate'] === todayLocal()
  function pickDesiredDate(d: string) {
    setForm((f) => ({ ...f, desiredDate: d }))
    clearAuto('desiredDate')
    setErrors((e) => {
      if (!e['desiredDate']) return e
      const next = { ...e }
      delete next['desiredDate']
      return next
    })
  }

  const set = (k: string) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [k]: e.target.value }))
    clearAuto(k)
  }

  // 최근 배송지(기업 스코프). 실패해도 주문서는 그대로 — 보조 기능이라 조용히 비운다.
  const recent = useAsync(
    () => (companyId ? ordersApi.savedAddresses().catch(() => ({ items: [] })) : Promise.resolve({ items: [] })),
    [companyId],
  )
  const recentItems = recent.data?.items ?? []

  // 결제 방법 선택(BIZ-78). 선불은 누구나, 후불(월 청구)은 후불 자격자(현재 판정=승인기업 APPROVED)만.
  // /api/my-company는 COMPANY_ADMIN 전용이라 담당자(MEMBER)나 조회 실패 시엔 승인상태를 모른다 —
  // 그럴 땐 선불을 강제하지 않고 후불 경로로 접수만 한다(결제 자동 트리거 금지). 선불이 꼭 필요한
  // 주문이면 서버가 paymentRequired로 판정해 상세 배너·발주 가드로 강제하므로 안전하다.
  // 선불 강제는 "승인상태를 확실히 알았고 후불 자격이 없을 때"만 건다. (전용 신용 게이트는 BIZ-104)
  const myCompany = useAsync(
    () => (companyId ? myCompanyApi.get().catch(() => null) : Promise.resolve(null)),
    [companyId],
  )
  const approvalKnown = myCompany.data != null
  const canPostpay = myCompany.data?.company.postpaidStatus === 'ACTIVE'
  const forcedPrepaid = approvalKnown && !canPostpay // 후불 자격을 알고, 후불(ACTIVE)이 아니다(BIZ-149)
  const [payChoice, setPayChoice] = useState<'PREPAID' | 'POSTPAID' | null>(null)
  const payMethod: 'PREPAID' | 'POSTPAID' = forcedPrepaid ? 'PREPAID' : payChoice ?? 'POSTPAID'

  // 포인트(BIZ-74). 잔액·적립율은 담당자도 볼 수 있는 요약 엔드포인트로 받는다(실패해도 0으로 진행).
  const pointSummary = useAsync(
    () => (companyId ? pointsApi.summary().catch(() => null) : Promise.resolve(null)),
    [companyId],
  )
  const pointBalance = pointSummary.data?.available ?? 0 // 지금 쓸 수 있는(성숙·미만료) 잔액
  const earnRate = pointSummary.data?.earnRate ?? 0
  const discountRate = pointSummary.data?.discountRate ?? 0
  // 주문 총액(상품가 × 수량). 할인액·적립 예정액 미리보기에 쓴다.
  const orderTotal = selected ? selected.price * Math.max(1, Number(form['quantity']) || 1) : 0
  const pointRates = { earnRate, discountRate }
  const { earn: earnPreview, discount: discountPreview } = pointPrice(orderTotal, pointRates)
  // 적립 / 즉시할인 / 포인트 전액결제(REDEEM) 택1. 기본 적립.
  const [pointChoice, setPointChoice] = useState<'EARN' | 'DISCOUNT' | 'REDEEM'>('EARN')
  // 전액결제는 사용가능 잔액이 주문총액 이상일 때만. 수량 변경 등으로 조건이 깨지면 적립으로 되돌린다.
  const canRedeem = orderTotal > 0 && pointBalance >= orderTotal
  const pointOption: 'EARN' | 'DISCOUNT' | 'REDEEM' = pointChoice === 'REDEEM' && !canRedeem ? 'EARN' : pointChoice
  // 1단계 결제 방법 토글(적립/즉시할인). 둘 다 율이 있을 때만 고를 의미가 있다. 수정(amend)엔 포인트 선택이 없다.
  const showPayToggle = !amend && earnRate > 0 && discountRate > 0
  const cardMode: 'EARN' | 'DISCOUNT' = pointOption === 'DISCOUNT' ? 'DISCOUNT' : 'EARN'
  // 1단계 CTA에 실어 보여줄 결제 예정 금액(고른 결제 방법 기준, 수량 반영). 포인트 전액결제면 싣지 않는다.
  const step1PayText =
    showPayToggle && selected && pointOption !== 'REDEEM'
      ? `${(cardMode === 'DISCOUNT' ? orderTotal - discountPreview : orderTotal).toLocaleString('ko-KR')}원 · `
      : ''

  // 수신자 디지털 카드(BIZ-86). 스킨을 고르면 배송완료 때 수신자 휴대폰으로 카드 링크가 나간다.
  const cardSkinsAsync = useAsync(
    () => (occasion ? cardSkinsApi.list(occasion).catch(() => ({ items: [] })) : Promise.resolve({ items: [] })),
    [occasion],
  )
  const cardSkins = cardSkinsAsync.data?.items ?? []
  const [cardSkinId, setCardSkinId] = useState<number | null>(null)
  const [cardMsg, setCardMsg] = useState('')
  const selectedSkin = cardSkins.find((k) => k.id === cardSkinId) ?? null

  // 디지털 카드 펼침 상태: 사용자가 열었거나 이미 스킨을 골랐으면 자동으로 펼친다.
  const cardOpen = showCard || cardSkinId != null

  // 디지털 화환(BIZ-125/126. 옛 이름 '디지털 근조기' — 근조기는 별도 판매 상품으로 분리).
  // 배송지가 **제휴 장례식장**이면 화환 주문에 무료로 함께 올린다(부고장·장례식장 화면 노출).
  // 문구·보낸이는 리본을 그대로 쓴다(추가 입력 0). 제휴 식장이면 **기본 선택** — 사용자가 직접
  // 켜고 끈 뒤(wreathTouched)엔 그 선택을 존중한다. 디자인은 회사 설정이 기본값이고 주문서에서
  // 바꾸면 접수 때 회사 설정에 저장돼 다음 주문에도 적용된다.
  const [digitalWreathOn, setDigitalWreathOn] = useState(false)
  const [wreathTouched, setWreathTouched] = useState(false)
  // 제휴 식장 안내창(2단계 → 3단계 넘어갈 때 주문당 한 번).
  const [wreathNoticeOpen, setWreathNoticeOpen] = useState(false)
  const [wreathNoticeSeen, setWreathNoticeSeen] = useState(false)
  // 내 화환 디자인(서버 저장, BIZ-126). 조회 실패해도 기본 디자인으로 그린다.
  const wreathSetting = useAsync(
    () => (companyId ? digitalWreathApi.get().catch(() => null) : Promise.resolve(null)),
    [companyId],
  )
  // 이 주문에서 고른 디자인(또는 이어서 작성 시 주문에 저장된 디자인). 없으면 회사 설정을 따른다.
  const [pickedWreathDesign, setPickedWreathDesign] = useState<WreathDesignId | null>(null)
  // 이번 화면에서 사용자가 디자인을 직접 골랐는지 — 이때만 회사 설정에 저장한다(옛 주문을 수정하다
  // 그 주문의 예전 디자인이 회사 기본값을 덮어쓰지 않게).
  const [wreathDesignChanged, setWreathDesignChanged] = useState(false)
  const wreathDesign = getWreathDesign(pickedWreathDesign ?? wreathSetting.data?.designId)
  // 장례식장 연결(BIZ-90 S2). hallId = 명시 선택(피커·파서·최근 배송지·이어서 작성). 없으면 장소명을
  // 서버 명부와 정규화 정확 일치로 잇는다(포함 매칭 아님). 연결된 식장의 isPartner가 디지털 화환 제휴 판정,
  // rooms가 빈소 원탭 칩. 자유 입력은 그대로 — 연결이 안 돼도 주문은 된다.
  const wreathPlaceName = form.deliveryPlaceName.trim()
  useEffect(() => {
    const clear = () => {
      setLinkedHall(null)
      setHallRooms([])
      setHallBlockedIds([])
    }
    if (occasion !== 'FUNERAL') {
      clear()
      return
    }
    let alive = true
    const load = async () => {
      if (hallId != null) {
        const d = await funeralHallApi.get(hallId)
        if (!alive) return
        setLinkedHall(d.hall)
        setHallRooms(d.rooms)
        setHallBlockedIds(d.blockedProductIds)
        return
      }
      if (wreathPlaceName.length < 2) {
        clear()
        return
      }
      const r = await funeralHallApi.resolve(wreathPlaceName, form.deliveryAddress.trim() || undefined)
      if (!alive) return
      setLinkedHall(r.hall)
      if (!r.hall) {
        setHallRooms([])
        setHallBlockedIds([])
        return
      }
      const d = await funeralHallApi.get(r.hall.id)
      if (!alive) return
      setHallRooms(d.rooms)
      setHallBlockedIds(d.blockedProductIds)
    }
    // 타이핑 중 과요청을 막으려 300ms 디바운스(명시 선택은 즉시).
    const t = setTimeout(() => {
      load().catch(() => {
        if (alive) clear()
      })
    }, hallId != null ? 0 : 300)
    return () => {
      alive = false
      clearTimeout(t)
    }
    // 주소는 동명 식장 분기에만 쓰여 장소명이 바뀔 때만 다시 묻는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occasion, hallId, wreathPlaceName])
  const isPartnerHall = occasion === 'FUNERAL' && linkedHall?.isPartner === true
  // 디지털 화환은 제휴 식장이면 기본 선택. 사용자가 직접 켜고 끈 뒤엔 건드리지 않는다.
  useEffect(() => {
    if (isPartnerHall && !wreathTouched) setDigitalWreathOn(true)
  }, [isPartnerHall, wreathTouched])
  // 차단 판정(S3): 식장이 화환 전체를 안 받거나, 고른 상품이 그 식장 차단 목록에 있으면 접수 불가.
  // 주 흐름(URL 파싱)은 1단계에서 식장이 먼저 정해져 카탈로그부터 거르고, 폴백(2단계 수동 입력)은
  // 여기서 재검사해 안내 + 대체 상품 원탭 교체를 낸다. 서버도 접수 때 같은 규칙으로 한 번 더 막는다.
  const hallRefusesAll = occasion === 'FUNERAL' && linkedHall != null && !linkedHall.wreathAccepted
  const productBlockedAtHall =
    occasion === 'FUNERAL' && linkedHall != null && productId != null && hallBlockedIds.includes(productId)
  const hallBlocked = hallRefusesAll || productBlockedAtHall

  // 장례식장 피커(근조): 장소명을 치면 명부 검색 제안. 고르면 이름·주소·우편번호를 채우고 id를 잇는다.
  const [hallOpen, setHallOpen] = useState(false)
  const [hallSuggests, setHallSuggests] = useState<FuneralHallSummary[]>([])
  useEffect(() => {
    if (occasion !== 'FUNERAL' || hallId != null || wreathPlaceName.length < 1) {
      setHallSuggests([])
      return
    }
    let alive = true
    const t = setTimeout(() => {
      funeralHallApi
        .search(wreathPlaceName)
        .then((r) => alive && setHallSuggests(r.items))
        .catch(() => alive && setHallSuggests([]))
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [occasion, hallId, wreathPlaceName])
  // 식장을 연달아 고르면 앞 선택의 상세 조회가 늦게 끝나 뒤 선택의 주소를 덮을 수 있다 — 마지막 선택만 반영한다.
  const hallPickSeq = useRef(0)
  async function pickHall(h: FuneralHallSummary) {
    const seq = ++hallPickSeq.current
    setHallOpen(false)
    setHallId(h.id)
    clearAuto('deliveryPlaceName', 'deliveryAddress')
    let address = h.address ?? ''
    let zipcode = ''
    try {
      const d = await funeralHallApi.get(h.id)
      address = d.hall.address ?? address
      zipcode = d.hall.zipcode ?? ''
    } catch {
      /* 요약값으로 충분 */
    }
    if (seq !== hallPickSeq.current) return
    // 식장을 직접 고른 건 "여기로 보낸다"는 가장 확실한 신호라 주소를 명부 값으로 덮는다. 예전엔 주소가
    // 비어 있을 때만 채워, 최근 배송지를 먼저 누른 뒤 식장을 바꾸면 이전 곳의 주소가 남았다(오배송 위험).
    // 상세주소도 이전 곳의 것이라 비운다. 명부에 주소가 없으면 비워서 다시 입력하게 한다 — 다른 곳 주소가
    // 남는 것보다 낫다. (링크 파서가 이은 식장은 이 경로를 타지 않아 파서 주소는 그대로다.)
    setForm((f) => ({
      ...f,
      deliveryPlaceName: h.name,
      deliveryAddress: address,
      deliveryZipcode: zipcode,
      deliveryAddressDetail: '',
    }))
    if (address) {
      setErrors((e) => {
        if (!e['deliveryAddress']) return e
        const next = { ...e }
        delete next['deliveryAddress']
        return next
      })
    }
  }

  /** 다음 우편번호 검색을 열어 도로명(없으면 지번) 주소·우편번호를 채운다. 상세·장소명은 수동. */
  async function openAddressSearch() {
    try {
      await openPostcodeSearch(({ address, zipcode }) => {
        setForm((f) => ({ ...f, deliveryAddress: address, deliveryZipcode: zipcode }))
        clearAuto('deliveryAddress')
        setErrors((e) => {
          if (!e['deliveryAddress']) return e
          const next = { ...e }
          delete next['deliveryAddress']
          return next
        })
      })
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '주소 검색을 열 수 없습니다')
    }
  }

  /** 최근 배송지 한 건으로 배송지·상세·장소명·우편번호를 한 번에 채운다. */
  function fillFromSaved(a: SavedAddress) {
    hallPickSeq.current++ // 진행 중인 식장 선택이 이 배송지를 덮지 않게
    setHallId(a.funeralHallId ?? null) // 접수 당시 연결까지 되살린다(BIZ-90)
    setForm((f) => ({
      ...f,
      deliveryAddress: a.address,
      deliveryZipcode: a.zipcode ?? '',
      deliveryAddressDetail: a.addressDetail ?? '',
      deliveryPlaceName: a.placeName ?? '',
    }))
    clearAuto('deliveryAddress', 'deliveryPlaceName')
    setErrors((e) => {
      if (!e['deliveryAddress']) return e
      const next = { ...e }
      delete next['deliveryAddress']
      return next
    })
  }

  function pickOccasion(v: OccasionType) {
    setOccasion(v)
    setProductId(null) // 유형이 바뀌면 상품 선택 초기화
    setCardSkinId(null) // 스킨은 유형별이라 함께 초기화(BIZ-86)
    setDigitalWreathOn(false) // 디지털 화환은 근조 전용(BIZ-126)
    setWreathTouched(false)
    setHallId(null) // 장례식장 연결은 근조 전용(BIZ-90)
    setCardMsg('')
    setErrors({})
  }

  /** 링크(부고·청첩장 등)를 파싱해 주문 필드를 미리 채운다(BIZ-107 범용 파서).
   *  서버가 occasion을 자동 판별한다. 아직 유형을 안 골랐으면(홈 히어로에서 링크로 진입) 그 유형으로
   *  바로 전환하고, 이미 고른 유형과 다르면(2단계에서 붙여넣음, 회의 S4) 조용히 바꾸지 않고 묻는다 —
   *  유형이 바뀌면 고른 상품이 초기화되기 때문이다.
   *  urlArg를 주면 그 값으로(홈 히어로에서 붙여넣고 넘어온 경우), 없으면 입력칸 값으로 파싱한다. */
  async function loadFromLink(urlArg?: string) {
    // 링크 입력칸은 2단계에 있다. 홈에서 링크를 들고 들어온 경우(1단계) 그 칸의 안내가 안 보이니 상단 배너로도 알린다.
    const showLinkError = (msg: string) => {
      setObitError(msg)
      if (urlArg != null) setFormError(msg)
    }
    const url = (urlArg ?? obituaryUrl).trim()
    if (!url || obitBusy) return
    setObitBusy(true)
    setObitError('')
    setObitWarnings([])
    setObitConfirm([])
    setMournerSuggest('')
    try {
      const { parsed } = await obituaryApi.parse(url)
      // 읽어 온 값이 하나도 없으면(스크립트로 그리는 청첩장 등) 조용히 끝내지 말고 알린다. 유형 전환·장소 교체도
      // 하지 않는다 — 빈 결과로 이미 입력한 값을 지우면 안 된다.
      if (!hasLinkFields(parsed)) {
        // 파서가 이유를 알면(예식이 끝나 닫힌 청첩장 등) 그 이유를, 모르면 일반 안내를 보인다.
        showLinkError(parsed.warnings[0] ?? LINK_EMPTY_MSG)
        return
      }
      if (occasion != null && parsed.occasionType !== occasion) {
        setLinkConflict(parsed)
        return
      }
      applyParsedLink(parsed, true)
    } catch (err) {
      showLinkError(
        err instanceof ApiError ? err.message : '링크에서 정보를 불러오지 못했어요. 아래에서 직접 입력해 주세요.',
      )
    } finally {
      setObitBusy(false)
    }
  }

  /** 파싱 결과를 폼에 반영한다. adoptOccasion=false면 지금 유형을 유지하고 필드만 채운다(유형 충돌 시 '그대로'). */
  function applyParsedLink(parsed: ParsedLinkDto, adoptOccasion: boolean) {
    const LABELS: Record<string, string> = {
      deceasedName: '고인명',
      mournerName: '상주',
      deliveryPlaceName: '장소명',
      mortuaryRoom: '빈소 호실',
      deliveryAddress: '배송지 주소',
      desiredDate: '희망 배송일',
      eventTarget: '받는 분',
      eventName: '행사명',
      eventType: '행사 종류',
    }
    const via: 'parser' | 'ai' = parsed.source === 'llm' ? 'ai' : 'parser'
    const updates: Partial<FormState> = {}
    const auto: Record<string, 'parser' | 'ai'> = {}
    const confirm: string[] = []
    let mourner = ''
    for (const [key, field] of Object.entries(parsed.fields)) {
      if (!field) continue
      const formKey = LINK_FIELD_MAP[key]
      if (!formKey) continue // 폼에 대응 칸이 없는 파서 필드(eventDateTime 등)는 건너뛴다
      // 확신 낮은 상주명은 폼에 넣지 않는다(BIZ-128) — 오기입된 인명이 리본·알림에 실리는
      // 해악이 공란보다 크다. 제안 칩으로만 보여주고 사용자가 확인 후 탭해서 채운다.
      if (key === 'mournerName' && field.confidence === 'low') {
        mourner = field.value
        continue
      }
      updates[formKey] = field.value
      auto[formKey] = via
      if (field.confidence === 'low') confirm.push(LABELS[key] ?? key)
    }
    // 발인·예식일이 이미 지났으면 그 과거 날짜를 희망 배송일로 쓸 수 없다 — 오늘로 보정한다
    // (지남 경고는 서버 warnings로 별도 표시된다).
    if (updates.desiredDate && updates.desiredDate < todayLocal()) {
      updates.desiredDate = todayLocal()
    }
    const nextOccasion = adoptOccasion ? parsed.occasionType : occasion
    // 장소(장소명·주소·우편번호·상세주소·빈소)는 한 묶음이다. 링크가 새 장소를 알려주면 묶음째 갈아
    // 끼운다 — 예전엔 링크에 있는 칸만 덮어써, 먼저 고른 식장·최근 배송지의 주소·상세주소·빈소가 새
    // 식장 이름 옆에 그대로 남았다(오배송 위험). 서버가 명부 식장과 이어 줬으면 빈 칸은 명부 값으로 채운다.
    const linkHall = nextOccasion === 'FUNERAL' ? parsed.funeralHall ?? null : null
    let hallAddressFromRegistry = false
    if (updates.deliveryPlaceName != null || updates.deliveryAddress != null || linkHall != null) {
      if (updates.deliveryPlaceName == null) updates.deliveryPlaceName = linkHall?.name ?? ''
      if (updates.deliveryAddress == null) {
        updates.deliveryAddress = linkHall?.address ?? ''
        hallAddressFromRegistry = !!linkHall?.address
      }
      updates.deliveryZipcode = ''
      updates.deliveryAddressDetail = ''
      if (updates.mortuaryRoom == null) updates.mortuaryRoom = ''
    }
    // 앞서 누른 식장의 상세 조회가 늦게 끝나 방금 채운 링크 장소를 덮지 않게 한다.
    const seq = ++hallPickSeq.current
    if (linkHall && hallAddressFromRegistry) {
      // 주소를 명부에서 가져왔으면 우편번호도 명부 값으로 맞춘다(요약엔 우편번호가 없다).
      funeralHallApi
        .get(linkHall.id)
        .then((d) => {
          if (seq !== hallPickSeq.current || !d.hall.zipcode) return
          setForm((f) => (f.deliveryAddress === d.hall.address ? { ...f, deliveryZipcode: d.hall.zipcode ?? '' } : f))
        })
        .catch(() => {
          /* 우편번호는 선택 항목 */
        })
    }
    if (adoptOccasion && parsed.occasionType !== occasion) {
      // 유형이 바뀌면 유형별 선택(상품·카드 스킨·디지털 화환)은 새로 고른다 — pickOccasion과 같은 초기화.
      setOccasion(parsed.occasionType)
      setProductId(null)
      setCardSkinId(null)
      setCardMsg('')
      setDigitalWreathOn(false)
      setWreathTouched(false)
    }
    setForm((prev) => ({ ...prev, ...updates }))
    setAutoFilled(auto)
    setMournerSuggest(mourner)
    // 서버가 명부와 이어 준 장례식장(BIZ-90) — 결과 유형이 근조일 때만 의미가 있다.
    setHallId(nextOccasion === 'FUNERAL' ? parsed.funeralHall?.id ?? null : null)
    // 유형 판별이 애매하면 사용자가 근조/축하를 직접 확인하도록 안내 맨 앞에 붙인다.
    if (adoptOccasion && parsed.occasionConfidence === 'low') confirm.unshift('주문 종류')
    setObitConfirm(confirm)
    setObitWarnings([...parsed.warnings])
    setErrors({})
  }

  function goNext() {
    if (step === 1 && !canLeaveStep1) {
      setFormError('주문 유형과 상품을 선택하세요')
      return
    }
    if (step === 2 && hallBlocked) {
      setFormError(hallRefusesAll ? '이 장례식장은 화환을 받지 않아요' : '이 장례식장에서 받지 않는 상품이에요. 다른 상품을 골라 주세요')
      return
    }
    // 제휴 식장이면 3단계로 넘어가기 전에 디지털 화환 안내창을 한 번 띄운다(주문당 1회, 새 주문만).
    if (step === 2 && isPartnerHall && !amend && !wreathTouched && !wreathNoticeSeen) {
      setFormError('')
      setWreathNoticeOpen(true)
      return
    }
    setFormError('')
    setStep((s) => Math.min(STEPS.length, s + 1))
  }

  function buildPayload(submit: boolean): Record<string, unknown> | null {
    if (!occasion || productId == null) return null
    // payload 조립(유형·상품·수량 + 유형별 텍스트)은 위저드·바텀시트 공용 규칙을 쓴다.
    const payload = buildOrderBody(occasion, productId, form, submit)
    // 프리셋을 골랐으면 id를 보낸다 — 서버가 sender_name을 스냅샷한다(order 도메인).
    if (senderPresetId != null) payload['senderPresetId'] = senderPresetId
    // 장례식장 연결(BIZ-90): 명시 선택이든 장소명 resolve든 이어진 id를 보낸다(서버가 다시 검증).
    if (occasion === 'FUNERAL' && linkedHall) payload['funeralHallId'] = linkedHall.id
    // 거래처: 입력값이 등록된 거래처와 정확히 일치하면 id를 함께 보낸다(서버가 이름 스냅샷·링크).
    // 일치하지 않으면 partnerName 자유 입력만 스냅샷된다(BIZ-49).
    const partnerMatch = partners.find((p) => p.name === form['partnerName'].trim())
    if (partnerMatch) payload['partnerId'] = partnerMatch.id
    // 포인트 선택(BIZ-74)은 접수 때만 의미 있다. 할인은 서버가 잔액 재검증 후 전액결제 처리한다.
    if (submit) payload['pointOption'] = pointOption
    // 수신자 디지털 카드(BIZ-86). 임시저장에도 실어 이어서 작성 시 유지한다.
    if (cardSkinId != null) {
      payload['digitalCardSkinId'] = cardSkinId
      if (cardMsg.trim()) payload['digitalCardMessage'] = cardMsg.trim()
    }
    // 디지털 화환(BIZ-126) — 제휴 장례식장 배송 + 토글 on일 때만 싣는다(근조 전용).
    if (occasion === 'FUNERAL' && digitalWreathOn && isPartnerHall) {
      payload['digitalWreathDesign'] = wreathDesign.id
    }
    return payload
  }

  async function handle(submit: boolean) {
    setErrors({})
    setFormError('')
    if (companyId == null) {
      setFormError('기업 계정으로 로그인해야 주문할 수 있습니다')
      return
    }
    const payload = buildPayload(submit)
    if (!payload) {
      setStep(1)
      setFormError('주문 유형과 상품을 선택하세요')
      return
    }
    const parsed = createOrderRequest.safeParse(payload)
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.')
        if (key && !errs[key]) errs[key] = issue.message
      }
      setErrors(errs)
      setStep(earliestErrorStep(Object.keys(errs), step)) // 에러 있는 앞 단계로 이동
      setFormError('입력값을 확인하세요')
      focusFirstError()
      return
    }

    setBusy(true)
    try {
      const res = amend
        ? await ordersApi.amendBody(editId!, parsed.data)
        : draftId
          ? await ordersApi.updateDraft(draftId, parsed.data)
          : await ordersApi.create(companyId, parsed.data)
      // 주문서에서 바꾼 디지털 화환 디자인은 회사 설정으로 저장해 다음 주문에도 적용한다(회의 결정).
      // 고르기만 하고 떠난 경우까지 기본값이 바뀌지 않도록 저장이 성공한 뒤에만 반영한다. 실패해도
      // 주문은 이미 저장됐으니 조용히 넘어간다. 결제창(토스)으로 페이지가 떠나기 전에 끝낸다.
      const sentDesign = parsed.data.digitalWreathDesign
      if (wreathDesignChanged && sentDesign && sentDesign !== wreathSetting.data?.designId) {
        await digitalWreathApi.update(sentDesign).catch(() => undefined)
      }
      // 선불을 고른 주문은 접수 직후 바로 결제창으로 잇는다. 성공하면 토스 successUrl로 페이지가
      // 떠나 아래 navigate에 못 온다. 창을 닫거나 실패하면 상세로 보내고, 거기 결제 배너로
      // 재시도한다(미완료 건 복구). 후불이거나 수정(amend)이면 결제 없이 접수만. 할인/적립 둘 다
      // 카드/후불로 결제한다(할인은 결제금액이 줄어든 것뿐).
      // 포인트 전액결제(REDEEM)면 결제금액이 0이라 토스 결제창을 띄우지 않는다(서버가 이미 완납 처리).
      if (submit && !amend && payMethod === 'PREPAID' && pointOption !== 'REDEEM') await startPrepaid(res.order.id)
      // 착지 피드백(1회성): 상세가 이 state를 읽어 완료 배너를 띄운다(모달 대신 착지 확인 — 국룰).
      navigate(`/orders/${res.order.id}`, {
        replace: true,
        state: amend ? { amended: true } : { submitted: true },
      })
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message)
        if (err.fields) {
          setErrors(err.fields)
          setStep(earliestErrorStep(Object.keys(err.fields), step))
          focusFirstError()
        }
      } else {
        setFormError('일시적인 오류가 발생했습니다')
      }
      setBusy(false)
    }
  }

  /**
   * 선불을 고른 주문은 접수 직후 토스 결제창을 띄운다(BIZ-78). 성공하면 페이지가 토스 successUrl로
   * 떠나 이 함수는 돌아오지 않는다. 창을 닫거나(취소) 결제 시작이 실패해도(키 미설정·네트워크) 주문은
   * 이미 접수됐으니 조용히 반환 → 호출부가 상세로 보내고 거기 결제 배너가 재시도를 안내한다.
   * 결제 생성은 서버가 살아있는 결제를 재사용해(payment_one_live_per_order) 이중결제가 없다.
   */
  async function startPrepaid(orderId: number): Promise<void> {
    try {
      const { payment } = await paymentsApi.create(orderId)
      await startTossPayment({
        orderId,
        orderKey: payment.orderKey,
        amount: payment.amount,
        orderName: `${selected?.name ?? '화환'} 주문`,
      })
    } catch (err) {
      if (!isTossUserCancel(err)) {
        // 취소가 아닌 진짜 오류도 여기선 삼킨다 — 상세 배너가 다시 안내하므로 접수 흐름은 막지 않는다.
      }
    }
  }

  // ── 자동 임시저장(A안) — 유형·상품을 고른 뒤부터 폼 변경 시 잠깐 뒤 자동 저장(디바운스).
  //    사용자는 버튼을 안 눌러도 되고, amend(접수건 수정)·제출 중엔 하지 않는다.
  async function autosave() {
    if (amend || busy || companyId == null || !draftChecked) return
    const payload = buildPayload(false)
    if (!payload) return // 유형·상품 미선택이면 저장할 게 없음
    const parsed = createOrderRequest.safeParse(payload)
    if (!parsed.success) return // 부분 입력이 아직 계약을 못 맞추면 다음 변경 때 재시도
    setSaveStatus('saving')
    try {
      const res = draftId
        ? await ordersApi.updateDraft(draftId, parsed.data)
        : await ordersApi.create(companyId, parsed.data)
      if (draftId == null) setDraftId(res.order.id)
      setSaveStatus('saved')
    } catch {
      setSaveStatus('idle') // 조용히 실패, 다음 변경 때 재시도
    }
  }

  useEffect(() => {
    if (amend || occasion == null || productId == null) return
    const t = setTimeout(() => void autosave(), 1500)
    return () => clearTimeout(t)
    // 폼·유형·상품·프리셋·애드온(카드·디지털 화환) 변경 시 디바운스 재설정. autosave는 매 렌더 최신 클로저.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, occasion, productId, senderPresetId, cardSkinId, cardMsg, digitalWreathOn, pickedWreathDesign])

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    // 마지막 단계 전에는 엔터·다음 버튼이 접수가 아니라 단계 이동을 뜻한다.
    if (step < STEPS.length) {
      goNext()
      return
    }
    void handle(true)
  }

  if (companyId == null) {
    return (
      <div className={s.page}>
        <h1 className="t-h36">주문하기</h1>
        <p className={s.lead}>기업 담당자 계정으로 로그인해야 주문할 수 있습니다.</p>
      </div>
    )
  }

  const head = amend
    ? {
        title: '주문 정보를 고쳐요',
        lead: textOnly
          ? '배송지·문구 등 텍스트 정보를 수정해요 (상품·수량은 발주 후 변경 불가 — 변경 시 업체에 자동 통지).'
          : '배송이 시작되기 전까지 주문 정보를 고칠 수 있어요.',
      }
    : STEP_HEADS[step - 1]!

  return (
    <div className={s.page} style={footerH ? { paddingBottom: footerH + 20 } : undefined}>
      {/* ── 진행 표시: 체크 원 + 라인(완료 단계는 클릭해 되돌아감) ── */}
      <div className={s.progA} aria-label="주문 진행">
        {STEPS.map((label, i) => {
          const n = i + 1
          const done = n < step
          const current = n === step
          return (
            <div key={label} className={s.progAItem}>
              <button
                type="button"
                className={s.progACircle}
                data-state={done ? 'done' : current ? 'now' : 'future'}
                onClick={() => (done ? setStep(n) : undefined)}
                aria-current={current ? 'step' : undefined}
                aria-label={`${n}단계 ${label}`}
                disabled={!done && !current}
              >
                {done ? CHECK : current ? <span className={s.progADot} /> : null}
              </button>
              {i < STEPS.length - 1 && (
                <span className={s.progALine} data-fill={n < step ? 'true' : 'false'} />
              )}
            </div>
          )
        })}
      </div>

      {/* ── 큰 친근한 단계 제목 ── */}
      <div className={s.head}>
        <h1 className="t-h36">{head.title}</h1>
        <p className={s.lead}>{head.lead}</p>
      </div>

      {formError && (
        <div className={s.error} role="alert">
          {formError}
        </div>
      )}

      <form className={s.form} onSubmit={onSubmit} noValidate>
        {/* ── 1단계: 유형·상품 ── */}
        {step === 1 && (
          <>
            {/* 작성 중이던 초안 안내 — 임시저장은 1슬롯이라 여기서 저장하면 그 초안을 덮는다.
                autosave가 그 초안을 재사용하기 시작하면(draftId 확정) 안내를 접는다. */}
            {!amend && priorDraft && draftId == null ? (
              <button
                type="button"
                className={s.priorDraft}
                onClick={() => navigate(`/orders/${priorDraft.id}/edit`)}
              >
                <span className={s.priorDraftIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <path d="m13.5 6.5 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <span className={s.priorDraftLabel}>
                  작성 중이던 주문이 있어요
                  {priorDraft.title ? ` — ${priorDraft.title}` : ''}
                </span>
                <span className={s.priorDraftGo}>이어서 작성 →</span>
              </button>
            ) : null}
            {/* 홈에서 링크를 붙여넣고 들어온 경우 — 파싱은 여기서 돌지만 결과 확인은 2단계(링크 칸)에 있다. */}
            {!amend && (obitBusy || Object.keys(autoFilled).length > 0) ? (
              <p className={s.help}>
                {obitBusy ? '링크에서 정보를 불러오는 중이에요…' : '링크에서 불러온 정보는 다음 단계에서 확인할 수 있어요.'}
              </p>
            ) : null}
            <div className={s.group}>
              <span className={s.label}>주문 유형</span>
              <div className={s.typeTabs} role="radiogroup" aria-label="주문 유형">
                {OCCASION_SEG.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={occasion === o.value}
                    className={occasion === o.value ? 'ptab on' : 'ptab'}
                    onClick={() => pickOccasion(o.value)}
                    disabled={textOnly}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {occasion && (
              <div className={s.group}>
                <span className={s.label}>상품 선택</span>
                {favByOccasion.length > 0 ? (
                  <div className={s.favToggle}>
                    {showAllProducts ? (
                      <button type="button" onClick={() => setShowAllProducts(false)}>
                        ★ 즐겨찾기만 보기 ({favByOccasion.length})
                      </button>
                    ) : (
                      <button type="button" onClick={() => setShowAllProducts(true)}>
                        전체 상품 보기 ({byOccasion.length}) →
                      </button>
                    )}
                  </div>
                ) : null}
                {catalog.loading ? (
                  <p className={s.help}>상품을 불러오는 중…</p>
                ) : byOccasion.length === 0 ? (
                  <p className={s.help}>이 유형으로 주문 가능한 상품이 없습니다. 운영자에게 문의하세요.</p>
                ) : (
                  <>
                  {/* 결제 방법 먼저(회의 S3, 시안 B): 적립/즉시할인을 고르면 카드가 실제 낼 금액으로 바뀐다.
                      4단계 포인트 옵션과 같은 상태(pointChoice)라 여기서 고른 게 결제 단계로 그대로 이어진다. */}
                  {showPayToggle ? (
                    <div className={s.payToggle} role="radiogroup" aria-label="결제 방법">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={cardMode === 'EARN'}
                        className={cardMode === 'EARN' ? `${s.payToggleOpt} ${s.payToggleOn}` : s.payToggleOpt}
                        onClick={() => setPointChoice('EARN')}
                      >
                        적립 받기 <span className={s.payToggleRate}>+{Math.round(earnRate * 100)}%</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={cardMode === 'DISCOUNT'}
                        className={cardMode === 'DISCOUNT' ? `${s.payToggleOpt} ${s.payToggleOn}` : s.payToggleOpt}
                        onClick={() => setPointChoice('DISCOUNT')}
                      >
                        즉시할인 <span className={s.payToggleRate}>−{Math.round(discountRate * 100)}%</span>
                      </button>
                    </div>
                  ) : null}
                  <div className={s.productGrid} role="radiogroup" aria-label="상품">
                    {shownProducts.map((it: CatalogItem, i) => {
                      const on = productId === it.productId
                      // 고른 결제 방법 기준으로 실제 낼 금액을 크게 보여준다(즉시할인이면 정가에 취소선, 적립이면 적립 포인트).
                      const pp = pointPrice(it.price, pointRates)
                      const showDisc = cardMode === 'DISCOUNT' && pp.discount > 0
                      return (
                        <div key={it.productId} className={s.productItem} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={on}
                            className={on ? `democard feat ${s.product}` : `democard ${s.product}`}
                            onClick={() => setProductId(it.productId)}
                            disabled={textOnly}
                          >
                            <span
                              className={s.productImg}
                              style={{ backgroundImage: `url(${it.imageUrl ?? defaultProductImage})` }}
                              aria-hidden="true"
                            />
                            <span className={s.productBody}>
                              <span className={s.productName}>{it.name}</span>
                              {showDisc ? (
                                <>
                                  <span className={s.productStrike}>{it.price.toLocaleString()}원</span>
                                  <span className={`${s.productPrice} ${s.productPriceDisc}`}>
                                    {pp.discounted.toLocaleString('ko-KR')}원
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className={s.productPrice}>{it.price.toLocaleString()}원</span>
                                  {pp.earn > 0 && cardMode === 'EARN' ? (
                                    <span className={s.productEarn}>+{pp.earn.toLocaleString('ko-KR')}P 적립</span>
                                  ) : null}
                                </>
                              )}
                            </span>
                          </button>
                          {/* 상세는 페이지 이동 대신 시트로 — 작성 중인 주문서를 잃지 않는다. */}
                          <button
                            type="button"
                            className={s.productMore}
                            onClick={() => setDetailId(it.productId)}
                            aria-label={`${it.name} 상세 보기`}
                          >
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                              <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                  </>
                )}
                {errors['productId'] && <p className="ferr">{errors['productId']}</p>}
              </div>
            )}

            {selected && (
              <div className={s.two}>
                <Field label="수량" error={errors['quantity']}>
                  <input type="number" min={1} max={999} value={form['quantity']} onChange={set('quantity')} disabled={busy || textOnly} />
                </Field>
              </div>
            )}
          </>
        )}

        {/* ── 2단계: 배송·행사 정보 ── */}
        {step === 2 && (
          <>
            {/* 링크로 빠르게 채우기(BIZ-107) — 회의 S4로 2단계(받는 곳 정보) 맨 위로 옮겼다. 붙여넣으면 장소·일자·
                받는 분을 채운다. 링크가 다른 유형이면 조용히 바꾸지 않고 묻는다(linkConflict 시트). */}
            {!amend && (
              <div className={s.group}>
                <span className={s.label}>링크로 빠르게 채우기 (선택)</span>
                <div className={s.inlineRow}>
                  <span className={s.clearField}>
                    <input
                      ref={linkInputRef}
                      aria-label="주문 링크"
                      value={obituaryUrl}
                      onChange={(e) => setObituaryUrl(e.target.value)}
                      placeholder="링크 붙여넣기"
                      disabled={obitBusy || busy}
                    />
                    {obituaryUrl && !obitBusy && !busy && (
                      // 링크 칸만 비운다 — 이미 채워진 주문 값과 거기 딸린 경고(발인 지남·화환 불가)는 그대로 둔다.
                      <button
                        type="button"
                        className={s.clearBtn}
                        aria-label="링크 지우기"
                        onClick={() => {
                          setObituaryUrl('')
                          setObitError('')
                          linkInputRef.current?.focus()
                        }}
                      >
                        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                          <circle cx="8" cy="8" r="8" fill="currentColor" />
                          <path d="M5.5 5.5l5 5m0-5l-5 5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn sec"
                    onClick={() => void loadFromLink()}
                    disabled={obitBusy || busy || !obituaryUrl.trim()}
                  >
                    {obitBusy ? '불러오는 중…' : '불러오기'}
                  </button>
                </div>
                {obitError && <p className="ferr">{obitError}</p>}
                {obitWarnings.length > 0 && (
                  <div className={s.warn} role="alert">
                    {obitWarnings.map((w, i) => (
                      <div key={i}>{w}</div>
                    ))}
                  </div>
                )}
                {obitConfirm.length > 0 && (
                  <p className={s.help}>
                    {Object.values(autoFilled).includes('ai')
                      ? 'AI가 링크에서 자동으로 입력했어요.'
                      : '자동으로 채웠어요.'}{' '}
                    <strong>{obitConfirm.join(', ')}</strong> 항목은 맞는지 꼭 확인해 주세요.
                  </p>
                )}
                {mournerSuggest ? (
                  <p className={s.help}>상주명은 확실하지 않아 비워 뒀어요 — 근조 정보에서 제안을 확인해 주세요.</p>
                ) : null}
              </div>
            )}
            {recentItems.length > 0 && (
              <div className={s.group}>
                <span className={s.label}>최근 배송지</span>
                <div className={s.chips}>
                  {recentItems.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={s.addrChip}
                      onClick={() => fillFromSaved(a)}
                      disabled={busy}
                    >
                      {a.placeName ? <strong>{a.placeName}</strong> : null}
                      <span>
                        {a.address}
                        {a.addressDetail ? ` ${a.addressDetail}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* 근조는 '장례식장'이 주인공 — 맨 위 큰 검색. 고르면 주소·빈소·제휴여부가 딸려온다(hall-first). */}
            {occasion === 'FUNERAL' && (
              <Field label="장례식장" error={errors['deliveryPlaceName']} auto={autoFilled['deliveryPlaceName']}>
                {/* 장례식장 피커(BIZ-90): 명부 검색 제안 + 자유 입력. 고르면 주소까지 채우고 id를 잇는다. */}
                <div className={s.combo}>
                  <input
                    value={form['deliveryPlaceName']}
                    onChange={(e) => {
                      set('deliveryPlaceName')(e)
                      setHallId(null) // 다시 치면 명시 선택 해제 → 장소명 resolve로 폴백
                      setHallOpen(true)
                    }}
                    onFocus={() => setHallOpen(true)}
                    onBlur={() => setHallOpen(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setHallOpen(false)
                    }}
                    placeholder="장례식장 이름 검색 (예: 서울성모장례식장)"
                    disabled={busy}
                    role="combobox"
                    aria-expanded={hallOpen && hallSuggests.length > 0}
                    aria-autocomplete="list"
                  />
                  {hallOpen && hallSuggests.length > 0 ? (
                    <div className={s.comboList} role="listbox">
                      {hallSuggests.map((h) => (
                        <button
                          key={h.id}
                          type="button"
                          role="option"
                          aria-selected={linkedHall?.id === h.id}
                          className={s.comboOpt}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            void pickHall(h)
                          }}
                        >
                          <span className={s.hallOptName}>{h.name}</span>
                          {h.address ? <span className={s.hallOptAddr}>{h.address}</span> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                {linkedHall?.isPartner ? (
                  <p className={s.hallLinked}>
                    <span className={s.hallLinkedDot} aria-hidden="true" />
                    디지털 화환을 무료로 함께 올릴 수 있는 장례식장이에요.
                  </p>
                ) : null}
                {/* 차단 안내(S3): 식장 전체 거부 → 고객센터, 상품 차단 → 대체 상품 원탭 교체. 해결 전 '다음' 막힘. */}
                {hallRefusesAll ? (
                  <div className={s.hallBlock} role="alert">
                    <strong>{linkedHall!.name}</strong>은 화환을 받지 않는 곳이에요. 다른 방법이 필요하면 고객센터로 연락해
                    주세요.
                    <a href="tel:1688-4318" className={s.hallBlockCall}>
                      고객센터 1688-4318
                    </a>
                  </div>
                ) : productBlockedAtHall ? (
                  <div className={s.hallBlock} role="alert">
                    <strong>{linkedHall!.name}</strong>은{' '}
                    <strong>{byOccasion.find((i) => i.productId === productId)?.name ?? items.find((i) => i.productId === productId)?.name ?? '이 상품'}</strong>
                    을 받지 않아요. 대신 보낼 상품을 골라 주세요.
                    {byOccasion.length > 0 ? (
                      <div className={s.chips}>
                        {byOccasion.slice(0, 6).map((i) => (
                          <button
                            key={i.productId}
                            type="button"
                            className={s.chip}
                            onClick={() => {
                              setProductId(i.productId)
                              setFormError('')
                            }}
                            disabled={busy}
                          >
                            {i.name} · {i.price.toLocaleString('ko-KR')}원
                          </button>
                        ))}
                      </div>
                    ) : (
                      <a href="tel:1688-4318" className={s.hallBlockCall}>
                        고객센터 1688-4318
                      </a>
                    )}
                  </div>
                ) : null}
              </Field>
            )}

            {/* 배송지 주소: 근조는 장례식장 고르면 자동으로 채워짐(수정 가능). 자유 입력 + 주소 검색 보조. */}
            <div className={s.group}>
              <span className={s.label}>
                배송지 주소
                <AutoBadge via={autoFilled['deliveryAddress']} />
              </span>
              <div className={s.inlineRow}>
                <input
                  aria-label="배송지 주소"
                  value={form['deliveryAddress']}
                  onChange={set('deliveryAddress')}
                  disabled={busy}
                />
                <button type="button" className="btn sec" onClick={() => void openAddressSearch()} disabled={busy}>
                  주소 검색
                </button>
              </div>
              {errors['deliveryAddress'] && <p className="ferr">{errors['deliveryAddress']}</p>}
            </div>

            {/* 근조 외 유형은 장소명(행사장)을 주소 다음에 둔다. */}
            {occasion !== 'FUNERAL' && (
              <Field label="장소명 (행사장, 선택)" error={errors['deliveryPlaceName']} auto={autoFilled['deliveryPlaceName']}>
                <input value={form['deliveryPlaceName']} onChange={set('deliveryPlaceName')} disabled={busy} />
              </Field>
            )}

            {/* 희망 배송일(회의 S4): '즉시'(=오늘) 원탭 또는 달력. 네이티브 date 입력 대신 공휴일이 보이는 달력 시트. */}
            <div className={s.group}>
              <span className={s.label} id="desired-date-label">
                희망 배송일
                <AutoBadge via={autoFilled['desiredDate']} />
              </span>
              <div className={s.dateSeg} role="group" aria-labelledby="desired-date-label">
                <button
                  type="button"
                  className={isTodayDate ? `${s.dateOpt} ${s.dateOptOn}` : s.dateOpt}
                  aria-pressed={isTodayDate}
                  onClick={() => pickDesiredDate(todayLocal())}
                  disabled={busy}
                >
                  <span className={s.dateOptMain}>즉시</span>
                  <span className={s.dateOptSub}>오늘 {fmtDateKo(todayLocal())}</span>
                </button>
                <button
                  type="button"
                  className={!isTodayDate && form['desiredDate'] ? `${s.dateOpt} ${s.dateOptOn}` : s.dateOpt}
                  aria-pressed={!isTodayDate && !!form['desiredDate']}
                  aria-label="희망 배송일 달력 열기"
                  onClick={() => setDateSheetOpen(true)}
                  disabled={busy}
                >
                  <span className={s.dateOptMain}>
                    {!isTodayDate && form['desiredDate'] ? fmtDateKo(form['desiredDate']) : '날짜 선택'}
                  </span>
                  <span className={s.dateOptSub}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4 10h16M9 3v4M15 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    달력에서 고르기
                  </span>
                </button>
              </div>
              {errors['desiredDate'] && <p className="ferr">{errors['desiredDate']}</p>}
            </div>

            {/* 유형별 정보 — 고른 유형의 필드만 점진 공개 */}
            <div className={s.section}>
              <div className={s.sectionLabel}>
                {occasion === 'FUNERAL' ? '근조 정보' : occasion === 'CELEBRATION' ? '축하 정보' : '수령 정보'}
              </div>
              {occasion === 'FUNERAL' && (
                <FuneralInfoFields
                  form={form}
                  set={set}
                  errors={errors}
                  busy={busy}
                  auto={autoFilled}
                  mournerSuggest={mournerSuggest}
                  rooms={hallRooms}
                  onRoomPick={(name) => {
                    setForm((f) => ({ ...f, mortuaryRoom: name }))
                    clearAuto('mortuaryRoom')
                  }}
                  onMournerPick={() => {
                    // 사용자가 확인하고 탭한 값 — 자동 입력이 아니라 배지는 안 단다.
                    setForm((f) => ({ ...f, mournerName: mournerSuggest }))
                    setMournerSuggest('')
                  }}
                />
              )}
              {occasion === 'CELEBRATION' && (
                <CelebrationInfoFields form={form} set={set} errors={errors} busy={busy} auto={autoFilled} />
              )}
              {occasion === 'GIFT' && <GiftInfoFields form={form} set={set} errors={errors} busy={busy} auto={autoFilled} />}
            </div>

            {/* 추가 정보(선택) — 기본 접힘으로 인풋 벽 완화. 값·에러 있으면 자동으로 펼침. */}
            {moreOpen ? (
              <div className={s.moreFields}>
                <Field label="상세 주소 (선택)" error={errors['deliveryAddressDetail']}>
                  <input value={form['deliveryAddressDetail']} onChange={set('deliveryAddressDetail')} disabled={busy} />
                </Field>
                <Field label="거래처 (선택, 리포트 집계 기준)" error={errors['partnerName']}>
                  {/* datalist는 스타일이 안 먹어 브라우저마다 제각각이라, 토큰으로 그린 커스텀 제안 목록을 쓴다.
                      자유 입력은 그대로 — 목록은 보조일 뿐이고, 등록 거래처와 정확히 일치할 때만 id가 링크된다. */}
                  <div className={s.combo}>
                    <input
                      value={form['partnerName']}
                      onChange={(e) => {
                        set('partnerName')(e)
                        setPartnerOpen(true)
                      }}
                      onFocus={() => setPartnerOpen(true)}
                      onBlur={() => setPartnerOpen(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setPartnerOpen(false)
                      }}
                      maxLength={100}
                      placeholder="등록된 거래처를 고르거나 직접 입력 (예: (주)하늘꽃상조)"
                      disabled={busy}
                      role="combobox"
                      aria-expanded={partnerOpen}
                      aria-autocomplete="list"
                    />
                    {partnerOpen && (partnerSuggests.length > 0 || canAddPartner) ? (
                      <div className={s.comboList} role="listbox">
                        {partnerSuggests.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            role="option"
                            aria-selected={form['partnerName'] === p.name}
                            className={s.comboOpt}
                            // blur보다 먼저 실행되게 mousedown에서 잡는다(클릭이 blur로 죽지 않게).
                            onMouseDown={(e) => {
                              e.preventDefault()
                              setForm((f) => ({ ...f, partnerName: p.name }))
                              setPartnerOpen(false)
                            }}
                          >
                            {p.name}
                          </button>
                        ))}
                        {canAddPartner ? (
                          <button
                            type="button"
                            role="option"
                            aria-selected={false}
                            className={`${s.comboOpt} ${s.comboAdd}`}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              void addPartner()
                            }}
                          >
                            {partnerAddBusy ? '등록 중…' : `＋ '${partnerTrimmed}' 거래처로 등록`}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </Field>
                {/* 희망 시간대는 숨김 — 못 지킬 약속이라 분쟁 소지. 배송은 "발인 맞춤(예정)"으로 안내. */}
                <Field label="수령인 연락처 (선택)" error={errors['recipientPhone']}>
                  <input
                    value={form['recipientPhone']}
                    onChange={set('recipientPhone')}
                    placeholder="01012345678"
                    inputMode="tel"
                    disabled={busy}
                  />
                </Field>
                <Field label="배송 요청사항 (선택)" error={errors['deliveryNote']}>
                  <input value={form['deliveryNote']} onChange={set('deliveryNote')} disabled={busy} />
                </Field>
              </div>
            ) : (
              <button type="button" className={s.moreToggle} onClick={() => setShowMore(true)}>
                ＋ 추가 정보 (상세주소·거래처·연락처·요청사항)
              </button>
            )}
          </>
        )}

        {/* ── 3단계: 문구·확인 ── */}
        {step === 3 && (
          <>
            {/* 리본 카드(BIZ-97): 미리보기 + 문구/보내는사람을 한 덩어리로 묶어 번잡함 제거. */}
            {occasion && (
              <div className={s.ribbonCard}>
                <div className={s.ribbonCardTop}>
                  <span className={s.ribbonCardTitle}>리본은 이렇게 나가요</span>
                  <RibbonPreview
                    occasion={occasion}
                    line1={form['ribbonLine1']}
                    line2={form['ribbonLine2']}
                    senderName={form['senderName']}
                  />
                </div>
                <div className={s.ribbonCardBody}>
                  {/* 문구 — 아래 칩으로 자주 쓰는 문구 추천 */}
                  <div className={s.ribbonLine}>
                    <Field label="문구 (30자 이내)" error={errors['ribbonLine1']}>
                      <input
                        value={form['ribbonLine1']}
                        onChange={set('ribbonLine1')}
                        placeholder={ribbonPhrasePlaceholder(occasion, form['eventType'])}
                        disabled={busy}
                      />
                    </Field>
                    {phrasesFor(occasion).length > 0 ? (
                      <div className={s.chips}>
                        {phrasesFor(occasion).map((ph) => (
                          <button
                            key={ph}
                            type="button"
                            className={form['ribbonLine1'] === ph ? `${s.chip} ${s.chipOn}` : s.chip}
                            onClick={() => setForm((f) => ({ ...f, ribbonLine1: ph }))}
                            disabled={busy}
                          >
                            {ph}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {/* 보내는 사람 — 아래 칩으로 발신자 프리셋(즐겨찾기) */}
                  <div className={s.ribbonLine}>
                    <Field label="보내는 사람" error={errors['senderName'] || errors['ribbonLine2']}>
                      <input
                        value={form['senderName']}
                        onChange={(e) => {
                          // 직접 고치면 프리셋 선택을 풀고 그 문구를 그대로 쓴다(그 주문 한정 수정).
                          setForm((f) => ({ ...f, senderName: e.target.value }))
                          setSenderPresetId(null)
                        }}
                        placeholder={senderNamePlaceholder(occasion)}
                        disabled={busy}
                      />
                    </Field>
                    {presets.length > 0 ? (
                      <div className={s.chips}>
                        {presets.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className={senderPresetId === p.id ? `${s.chip} ${s.chipOn}` : s.chip}
                            onClick={() => {
                              setSenderPresetId(p.id)
                              setForm((f) => ({ ...f, senderName: p.senderName }))
                            }}
                            disabled={busy}
                          >
                            {p.senderName}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={senderPresetId === null ? `${s.chip} ${s.chipOn}` : s.chip}
                          onClick={() => {
                            setSenderPresetId(null)
                            setForm((f) => ({ ...f, senderName: '' }))
                          }}
                          disabled={busy}
                        >
                          직접 입력
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
            {/* 디지털 화환(BIZ-125/126) — 제휴 장례식장이면 카드보다 위에, 접지 않고 보여준다(기본 선택).
                제휴가 아니면 올릴 곳이 없어 아예 그리지 않는다(2단계 식장 안내가 대신 알린다). */}
            {occasion === 'FUNERAL' && isPartnerHall ? (
              <div className={s.cardSection}>
                <div className={s.sectionLabel}>디지털 화환</div>
                <p className={s.cardHint}>
                  <strong>{linkedHall?.name ?? wreathPlaceName}</strong>은 제휴 장례식장이에요. 리본 문구와 보내는 분이
                  온라인 부고장과 장례식장 화면에 함께 올라가요 · 추가 비용 없음
                </p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={digitalWreathOn}
                  className={`${s.dwToggle} ${digitalWreathOn ? s.dwToggleOn : ''}`}
                  onClick={() => {
                    setDigitalWreathOn((v) => !v)
                    setWreathTouched(true)
                  }}
                  disabled={busy}
                >
                  <span className={s.dwToggleIcon} aria-hidden="true">
                    <Chrysanthemum />
                  </span>
                  <span className={s.dwToggleBody}>
                    <span className={s.dwToggleTitle}>디지털 화환 함께 보내기</span>
                    <span className={s.dwToggleSub}>부고장 · 장례식장 화면 노출</span>
                  </span>
                  <span className={s.dwSwitch} aria-hidden="true" />
                </button>
                {digitalWreathOn ? (
                  <div className={s.dwDesign}>
                    <div className={s.dwDesignLabel}>디자인 · 고른 디자인은 다음 주문에도 적용돼요</div>
                    <WreathDesignPicker
                      value={wreathDesign.id}
                      onChange={(id) => {
                        setPickedWreathDesign(id)
                        setWreathDesignChanged(true)
                      }}
                      disabled={busy}
                    />
                    <WreathPreview
                      design={wreathDesign}
                      message={form['ribbonLine1'].trim()}
                      from={form['ribbonLine2'].trim() || form['senderName'].trim()}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* 디지털 카드(선택 애드온) — 기본 접힘. 열거나 이미 고르면 펼쳐진다. */}
            {cardSkins.length > 0 && !cardOpen ? (
              <button type="button" className={s.moreToggle} onClick={() => setShowCard(true)}>
                ＋ 디지털 카드 함께 보내기 (선택)
              </button>
            ) : null}
            {/* 수신자 디지털 카드(BIZ-86) — 스킨을 고르면 배송완료 때 수신자에게 카드 링크가 나간다. */}
            {cardOpen && occasion && cardSkins.length > 0 ? (
              <div className={s.cardSection}>
                <div className={s.sectionLabel}>디지털 카드 (선택)</div>
                <p className={s.cardHint}>
                  배송이 완료되면 수령인 휴대폰으로 마음을 담은 카드 링크를 보내드려요.
                </p>
                {/* 가로 스크롤 비주얼 카드 행 — 디자인이 늘어도 한 줄로 유지(칩 뭉침 방지). */}
                <div className={s.cardPickRow} role="radiogroup" aria-label="디지털 카드 디자인">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={cardSkinId === null}
                    className={`${s.cardPick} ${cardSkinId === null ? s.cardPickOn : ''}`}
                    onClick={() => setCardSkinId(null)}
                    disabled={busy}
                  >
                    <span className={`${s.cardPickThumb} ${s.cardPickNone}`} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                        <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className={s.cardPickName}>안 보냄</span>
                  </button>
                  {cardSkins.map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      role="radio"
                      aria-checked={cardSkinId === k.id}
                      className={`${s.cardPick} ${cardSkinId === k.id ? s.cardPickOn : ''}`}
                      onClick={() => {
                        setCardSkinId(k.id)
                        // 메시지가 비어 있으면 스킨 기본 문구를 시작점으로 깔아준다.
                        if (!cardMsg.trim() && k.defaultMessage) setCardMsg(k.defaultMessage)
                      }}
                      disabled={busy}
                    >
                      <span
                        className={s.cardPickThumb}
                        style={{ background: k.bgColor, color: k.accentColor }}
                        aria-hidden="true"
                      >
                        <span className={s.cardPickLabel}>
                          {occasion === 'FUNERAL' ? '謹弔' : occasion === 'CELEBRATION' ? '祝賀' : '感謝'}
                        </span>
                        {cardSkinId === k.id ? <span className={s.cardPickCheck} aria-hidden="true">✓</span> : null}
                      </span>
                      <span className={s.cardPickName}>{k.name}</span>
                    </button>
                  ))}
                </div>
                {selectedSkin ? (
                  <div className={s.cardEditor}>
                    <Field label="카드 메시지 (300자 이내)" error={errors['digitalCardMessage']}>
                      <textarea
                        value={cardMsg}
                        onChange={(e) => setCardMsg(e.target.value)}
                        rows={5}
                        maxLength={300}
                        disabled={busy}
                      />
                    </Field>
                    {/* 미니 미리보기 — 수신자가 볼 카드의 톤을 그대로. 실제 카드는 모달에서 큰 화면으로. */}
                    <div className={s.cardPrevWrap}>
                      <div
                        className={s.cardPrev}
                        style={{ background: selectedSkin.bgColor, color: selectedSkin.textColor, borderColor: selectedSkin.accentColor }}
                        aria-label="카드 미리보기"
                      >
                        <div className={s.cardPrevLabel} style={{ color: selectedSkin.accentColor }}>
                          {occasion === 'FUNERAL' ? '謹弔' : occasion === 'CELEBRATION' ? '축하의 마음' : '감사의 마음'}
                        </div>
                        {form['ribbonLine1'].trim() ? <div className={s.cardPrevHead}>{form['ribbonLine1']}</div> : null}
                        <div className={s.cardPrevMsg}>{cardMsg || '카드 메시지가 여기에 들어가요'}</div>
                        <div className={s.cardPrevFrom} style={{ color: selectedSkin.accentColor }}>
                          {form['senderName'].trim() || '보내는 이'}
                        </div>
                      </div>
                      <button type="button" className={s.cardPrevBtn} onClick={() => setCardPreviewOpen(true)}>
                        실제 카드로 보기
                      </button>
                    </div>
                    {!form['recipientPhone'].trim() ? (
                      <p className={s.cardWarn}>수령인 연락처(2단계)를 입력해야 카드를 보낼 수 있어요.</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <Field label="내부 메모 (업체에 노출되지 않음, 선택)" error={errors['companyMemo']}>
              <input value={form['companyMemo']} onChange={set('companyMemo')} disabled={busy} />
            </Field>
          </>
        )}

        {/* ── 4단계: 결제·확인 (리뷰를 독립 스텝으로 — 오발송 방지) ── */}
        {step === 4 && (
          <>
            {/* 제출 전 확인 요약(BIZ-81): 받는 분 맨 위·금액 맨 아래. 오타·오발송을 눈으로 잡는다. */}
            {occasion && selected && (
              <div className={s.confirm} aria-label="주문 확인 요약">
                {buildConfirmRows(occasion, form, selected).map((r) => {
                  let cls = s.sumrow
                  if (r.cls === 'top') cls += ` ${s.sumTop}`
                  else if (r.cls === 'total') cls += ` ${s.sumTotal}`
                  return (
                    <div key={r.key} className={cls}>
                      <span className={s.sumL}>{r.label}</span>
                      <span className={s.sumR}>
                        {r.value}
                        {r.sub ? <span className={s.sumSub}>{r.sub}</span> : null}
                      </span>
                    </div>
                  )
                })}
                <p className={s.confirmNote}>배송이 시작되기 전까지는 주문 정보를 고칠 수 있어요.</p>
              </div>
            )}

            {/* 포인트(BIZ-74): 할인(즉시 할인) 또는 적립(정가+적립) 택1. 기본 적립. */}
            {!amend && orderTotal > 0 && (
              <fieldset className={s.payPick}>
                <legend className={s.payPickTitle}>
                  포인트 <span className={s.payOptSub}>사용가능 {pointBalance.toLocaleString('ko-KR')}P</span>
                </legend>
                <label className={`${s.payOpt} ${pointOption === 'EARN' ? s.payOptOn : ''}`}>
                  <input
                    type="radio"
                    name="pointOption"
                    checked={pointOption === 'EARN'}
                    onChange={() => setPointChoice('EARN')}
                  />
                  <span className={s.payOptBody}>
                    <span className={s.payOptMain}>적립 {earnRate > 0 ? `(${Math.round(earnRate * 100)}%)` : ''}</span>
                    <span className={s.payOptSub}>
                      {earnPreview > 0
                        ? `정가로 결제하고 ${earnPreview.toLocaleString('ko-KR')}P 적립`
                        : '정가 결제 (적립 없음)'}
                    </span>
                  </span>
                </label>
                <label className={`${s.payOpt} ${pointOption === 'DISCOUNT' ? s.payOptOn : ''}`}>
                  <input
                    type="radio"
                    name="pointOption"
                    checked={pointOption === 'DISCOUNT'}
                    onChange={() => setPointChoice('DISCOUNT')}
                  />
                  <span className={s.payOptBody}>
                    <span className={s.payOptMain}>즉시 할인 {discountRate > 0 ? `(${Math.round(discountRate * 100)}%)` : ''}</span>
                    <span className={s.payOptSub}>
                      {discountPreview > 0
                        ? `${discountPreview.toLocaleString('ko-KR')}원 할인 — ${(orderTotal - discountPreview).toLocaleString('ko-KR')}원 결제`
                        : '할인 없음'}
                    </span>
                  </span>
                </label>
                <label className={`${s.payOpt} ${pointOption === 'REDEEM' ? s.payOptOn : ''}`} aria-disabled={!canRedeem}>
                  <input
                    type="radio"
                    name="pointOption"
                    checked={pointOption === 'REDEEM'}
                    disabled={!canRedeem}
                    onChange={() => setPointChoice('REDEEM')}
                  />
                  <span className={s.payOptBody}>
                    <span className={s.payOptMain}>포인트로 전액 결제</span>
                    <span className={s.payOptSub}>
                      {canRedeem
                        ? `${orderTotal.toLocaleString('ko-KR')}P 사용 · 0원 결제`
                        : `${Math.max(0, orderTotal - pointBalance).toLocaleString('ko-KR')}P 더 모으면 사용 가능`}
                    </span>
                  </span>
                </label>
              </fieldset>
            )}

            {/* 포인트 전액결제면 카드/후불 결제가 필요 없다 — 결제 방법 선택을 숨긴다. */}
            {!amend && pointOption === 'REDEEM' && (
              <p className={s.confirmNote}>포인트로 전액 결제돼요 — 카드 결제 없이 접수됩니다.</p>
            )}

            {/* 결제 방법(BIZ-78): 선불은 누구나, 후불(월 청구)은 후불 자격자만. 수정 때·승인상태를
                모를 때(담당자 등)는 안 보인다 — 선불 필요는 서버가 배너로 안내한다. */}
            {!amend && approvalKnown && pointOption !== 'REDEEM' && (
              <fieldset className={s.payPick}>
                <legend className={s.payPickTitle}>결제 방법</legend>
                <label className={`${s.payOpt} ${payMethod === 'PREPAID' ? s.payOptOn : ''}`}>
                  <input
                    type="radio"
                    name="payMethod"
                    checked={payMethod === 'PREPAID'}
                    onChange={() => setPayChoice('PREPAID')}
                  />
                  <span className={s.payOptBody}>
                    <span className={s.payOptMain}>선불 · 카드 즉시결제</span>
                    <span className={s.payOptSub}>접수하면 결제창이 열려요</span>
                  </span>
                </label>
                <label
                  className={`${s.payOpt} ${payMethod === 'POSTPAID' ? s.payOptOn : ''} ${
                    canPostpay ? '' : s.payOptLocked
                  }`}
                >
                  <input
                    type="radio"
                    name="payMethod"
                    checked={payMethod === 'POSTPAID'}
                    disabled={!canPostpay}
                    onChange={() => setPayChoice('POSTPAID')}
                  />
                  <span className={s.payOptBody}>
                    <span className={s.payOptMain}>후불 · 월 청구</span>
                    <span className={s.payOptSub}>
                      {canPostpay ? '한 달 이용분을 모아 청구해요' : '승인된 기업만 이용할 수 있어요'}
                    </span>
                  </span>
                </label>
              </fieldset>
            )}
          </>
        )}

        {/* 접수 안내(마지막 단계) — 고정 바 위에 둔다(바 아래면 sticky와 겹친다) */}
        {step === STEPS.length ? (
          <p className={s.submitNote}>
            {amend
              ? '수정 내용은 바로 반영됩니다. 준비가 시작된 뒤에는 전화로 문의해 주세요.'
              : payMethod === 'PREPAID'
                ? '접수하면 결제창이 열립니다. 결제가 완료되면 주문번호가 부여되고 준비가 시작됩니다.'
                : '접수하면 주문번호가 부여되고 운영자 검토가 시작됩니다. 임시저장은 나중에 이어서 접수할 수 있습니다.'}
          </p>
        ) : null}

        {/* ── 뷰포트 하단 고정 CTA 바: 이전(아웃라인) + 다음/접수(솔리드). 저장은 자동 ── */}
        <div className={s.footer} ref={footerRef}>
          <div className={s.footerInner}>
            {/* 줄은 처음부터 잡아 둔다 — 첫 자동저장 때 줄이 생기며 바가 튀어 오르지 않게. */}
            {!amend ? (
              <div className={s.saveHint} aria-live="polite">
                {saveStatus === 'saving' ? '저장 중…' : saveStatus === 'saved' ? '임시저장됨' : ''}
              </div>
            ) : null}
            {step === 1 && showPayToggle ? (
              <p className={s.payCarry}>고른 결제 방법은 마지막 결제 단계에 그대로 이어져요</p>
            ) : null}
            <div className={s.ctaRow}>
              {step > 1 ? (
                <button type="button" className="btn sec" onClick={() => setStep((v) => v - 1)} disabled={busy}>
                  ← 이전
                </button>
              ) : null}
              <button
                type="submit"
                className={`btn ${s.cta}`}
                disabled={busy || (step === 1 && !canLeaveStep1) || (step === 2 && hallBlocked)}
              >
                {step < STEPS.length
                  ? `${step === 1 ? step1PayText : ''}다음 단계 →`
                  : busy
                    ? '처리 중…'
                    : amend
                      ? '수정 저장'
                      : payMethod === 'PREPAID'
                        ? '결제하고 주문 접수'
                        : '주문 접수'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* 희망 배송일 달력(회의 S4) — 오늘 이전은 막는다. 고르면 바로 닫힌다. */}
      {dateSheetOpen ? (
        <DatePickerSheet
          title="희망 배송일"
          value={form['desiredDate']}
          min={todayLocal()}
          onPick={(d) => {
            pickDesiredDate(d)
            setDateSheetOpen(false)
          }}
          onClose={() => setDateSheetOpen(false)}
        />
      ) : null}

      {/* 링크 유형 충돌(회의 S4) — 2단계에서 붙인 링크가 고른 유형과 다르면 조용히 바꾸지 않고 묻는다. */}
      {linkConflict && occasion ? (
        <BottomSheet
          title="다른 종류의 링크 같아요"
          onClose={() => setLinkConflict(null)}
          footer={
            <div className={s.sheetActions}>
              <button
                type="button"
                className="btn sec"
                onClick={() => {
                  applyParsedLink(linkConflict, false)
                  setLinkConflict(null)
                }}
              >
                {OCCASION_LABEL[occasion]} 그대로
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  applyParsedLink(linkConflict, true)
                  setLinkConflict(null)
                  setStep(1)
                  setFormError('유형이 바뀌었어요. 보낼 상품을 다시 골라 주세요')
                }}
              >
                {OCCASION_LABEL[linkConflict.occasionType]}로 바꾸기
              </button>
            </div>
          }
        >
          <p className={s.sheetLead}>
            이 링크는 <strong>{OCCASION_LABEL[linkConflict.occasionType]}</strong> 주문으로 보여요. 지금은{' '}
            <strong>{OCCASION_LABEL[occasion]}</strong>으로 작성 중이에요.
          </p>
          <p className={s.sheetNote}>
            바꾸면 1단계로 돌아가 상품을 다시 골라야 해요. 그대로 두면 장소·날짜 같은 정보만 채워요.
          </p>
        </BottomSheet>
      ) : null}

      {/* 상품 상세 시트 — 상세 페이지와 같은 신뢰정보(ProductInfo) + 결제 방법별 금액. 고르면 그 상품으로 선택. */}
      {detailItem ? (
        <BottomSheet
          title="상품 상세"
          onClose={() => setDetailId(null)}
          footer={
            <button
              type="button"
              className="btn"
              disabled={textOnly}
              onClick={() => {
                setProductId(detailItem.productId)
                setFormError('')
                setDetailId(null)
              }}
            >
              {productId === detailItem.productId ? '이 상품으로 선택됨 ✓' : '이 상품으로 고르기'}
            </button>
          }
        >
          <div
            className={s.detailImg}
            style={{ backgroundImage: `url(${detailItem.imageUrl ?? defaultProductImage})` }}
            role="img"
            aria-label={detailItem.name}
          />
          <ProductInfo product={detailItem} rates={pointRates} as="h3" />
        </BottomSheet>
      ) : null}

      {/* 제휴 장례식장 디지털 화환 안내창 — 2단계에서 다음을 누를 때 주문당 한 번. 고르면 3단계로 잇는다.
          닫기(X·바깥 탭)는 결정 보류 — 2단계에 머물고, 기본 선택(켬)은 그대로 둔 채 다시 묻지 않는다. */}
      {wreathNoticeOpen ? (
        <BottomSheet
          title="디지털 화환도 함께 올릴까요?"
          onClose={() => {
            setWreathNoticeOpen(false)
            setWreathNoticeSeen(true)
          }}
          footer={
            <div className={s.sheetActions}>
              <button
                type="button"
                className="btn sec"
                onClick={() => {
                  setDigitalWreathOn(false)
                  setWreathTouched(true)
                  setWreathNoticeOpen(false)
                  setWreathNoticeSeen(true)
                  setStep(3)
                }}
              >
                이번엔 빼기
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setDigitalWreathOn(true)
                  setWreathTouched(true)
                  setWreathNoticeOpen(false)
                  setWreathNoticeSeen(true)
                  setStep(3)
                }}
              >
                좋아요, 함께 보낼게요
              </button>
            </div>
          }
        >
          <p className={s.sheetLead}>
            <strong>{linkedHall?.name ?? wreathPlaceName}</strong>은 제휴 장례식장이에요. 화환과 함께 디지털 화환이
            온라인 부고장과 장례식장 화면에 올라가요. <strong>추가 비용은 없어요.</strong>
          </p>
          <WreathPreview
            design={wreathDesign}
            message={form['ribbonLine1'].trim()}
            from={form['ribbonLine2'].trim() || form['senderName'].trim()}
          />
          <p className={s.sheetNote}>문구는 리본과 같게 나가고, 디자인은 다음 단계에서 바꿀 수 있어요.</p>
        </BottomSheet>
      ) : null}

      {/* 디지털 카드 전체화면 미리보기 — 수신자가 보는 실제 카드(CardView)를 모바일 폭 그대로.
          admin 카드스킨 미리보기와 같은 렌더러라 실제와 어긋나지 않는다. */}
      {cardPreviewOpen && selectedSkin ? (
        <div className={s.cardModalOverlay} role="dialog" aria-modal="true" aria-label="카드 미리보기">
          <button type="button" className={s.cardModalBack} onClick={() => setCardPreviewOpen(false)}>
            ‹ 닫기
          </button>
          <div className={s.cardModalStage}>
            <CardView
              occasionType={occasion!}
              frame={selectedSkin.frame as CardFrame}
              bgColor={selectedSkin.bgColor}
              textColor={selectedSkin.textColor}
              accentColor={selectedSkin.accentColor}
              bgImageUrl={selectedSkin.bgImageUrl ?? null}
              textPosition={selectedSkin.textPosition as CardTextPosition}
              headline={form['ribbonLine1'].trim() || null}
              message={cardMsg.trim() || selectedSkin.defaultMessage || null}
              senderName={form['senderName'].trim() || null}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

interface FieldsProps {
  form: FormState
  set: (k: string) => (e: { target: { value: string } }) => void
  errors: Record<string, string>
  busy: boolean
  /** 링크 파서가 자동으로 채운 필드(BIZ-128) — 입력칸 라벨에 배지로 고지 */
  auto: Record<string, 'parser' | 'ai'>
}

function FuneralInfoFields({
  form,
  set,
  errors,
  busy,
  auto,
  mournerSuggest,
  onMournerPick,
  rooms,
  onRoomPick,
}: FieldsProps & {
  mournerSuggest: string
  onMournerPick: () => void
  /** 연결된 장례식장의 빈소 목록(BIZ-90) — 원탭 칩. 비면 안 그린다. */
  rooms: FuneralHallRoom[]
  onRoomPick: (name: string) => void
}) {
  const roomVal = form['mortuaryRoom'].trim()
  return (
    <>
      <Field label="빈소 (예: 특1호실)" error={errors['mortuaryRoom']} auto={auto['mortuaryRoom']}>
        <input value={form['mortuaryRoom']} onChange={set('mortuaryRoom')} disabled={busy} />
        {rooms.length > 0 ? (
          <div className={`${s.chips} ${s.roomChips}`} role="group" aria-label="빈소 선택">
            {rooms.map((r) => (
              <button
                key={r.id}
                type="button"
                className={roomVal === r.name ? `${s.chip} ${s.chipOn}` : s.chip}
                onClick={() => onRoomPick(r.name)}
                disabled={busy}
              >
                {r.name}
              </button>
            ))}
          </div>
        ) : null}
      </Field>
      <div className={s.two}>
        <Field label="고인명 (고인 또는 상주 중 하나)" error={errors['deceasedName']} auto={auto['deceasedName']}>
          <input value={form['deceasedName']} onChange={set('deceasedName')} disabled={busy} />
        </Field>
        <Field label="상주명" error={errors['mournerName']} auto={auto['mournerName']}>
          <input value={form['mournerName']} onChange={set('mournerName')} disabled={busy} />
        </Field>
      </div>
      {/* 확신 낮은 상주명은 자동 입력하지 않는다(BIZ-128) — 링크에서 본 이름을 제안만 하고,
          사용자가 확인 후 탭해서 채운다. 이미 직접 입력했으면 제안을 숨긴다. */}
      {mournerSuggest && !form['mournerName'] ? (
        <div className={s.mournerSuggest}>
          <span className={s.help}>링크에서 본 상주 이름 — 맞는지 확인 후 탭해서 입력</span>
          <button type="button" className={s.chip} onClick={onMournerPick} disabled={busy}>
            {mournerSuggest}
          </button>
        </div>
      ) : null}
    </>
  )
}

function CelebrationInfoFields({ form, set, errors, busy, auto }: FieldsProps) {
  return (
    <>
      <div className={s.two}>
        <Field label="행사 종류 (예: 개업)" error={errors['eventType']} auto={auto['eventType']}>
          <input value={form['eventType']} onChange={set('eventType')} disabled={busy} />
        </Field>
        <Field label="행사 대상자" error={errors['eventTarget']} auto={auto['eventTarget']}>
          <input value={form['eventTarget']} onChange={set('eventTarget')} disabled={busy} />
        </Field>
      </div>
      <Field label="행사명 (선택)" error={errors['eventName']} auto={auto['eventName']}>
        <input value={form['eventName']} onChange={set('eventName')} disabled={busy} />
      </Field>
    </>
  )
}

function GiftInfoFields({ form, set, errors, busy }: FieldsProps) {
  return (
    <>
      <div className={s.two}>
        <Field label="수령인명" error={errors['recipientName']}>
          <input value={form['recipientName']} onChange={set('recipientName')} disabled={busy} />
        </Field>
        <Field label="수령처 (회사·기관)" error={errors['recipientOrg']}>
          <input value={form['recipientOrg']} onChange={set('recipientOrg')} disabled={busy} />
        </Field>
      </div>
      <div className={s.two}>
        <Field label="부서 (선택)" error={errors['recipientDept']}>
          <input value={form['recipientDept']} onChange={set('recipientDept')} disabled={busy} />
        </Field>
        <Field label="직함 (선택)" error={errors['recipientTitle']}>
          <input value={form['recipientTitle']} onChange={set('recipientTitle')} disabled={busy} />
        </Field>
      </div>
    </>
  )
}

/** 디지털 화환 미리보기 — 부고장 '조문해주신 분' 카드의 축소 재현. 문구가 비면 예시로 채운다. */
function WreathPreview({ design, message, from }: { design: WreathDesign; message: string; from: string }) {
  const bar = { background: `linear-gradient(180deg, ${design.bar[0]} 0%, ${design.bar[1]} 100%)` }
  return (
    <div className={s.dwPrev} aria-label="디지털 화환 미리보기">
      <span className={s.dwPrevBar} style={bar} aria-hidden="true" />
      <div className={s.dwPrevInner}>
        <span className={s.dwPrevCircle} style={{ background: design.circleBg, color: design.flower }} aria-hidden="true">
          <Chrysanthemum />
        </span>
        <span className={s.dwPrevMsg}>{message || '삼가 고인의 명복을 빕니다'}</span>
        <span className={s.dwPrevFrom}>{from || '보내는 이'}</span>
      </div>
      <span className={s.dwPrevBar} style={bar} aria-hidden="true" />
    </div>
  )
}

/** 링크 파서 필드 키 → 폼 필드 키. eventDateTime은 폼에 별도 칸이 없다(배송일은 desiredDate가 담는다). */
const LINK_FIELD_MAP: Partial<Record<string, keyof FormState>> = {
  deliveryPlaceName: 'deliveryPlaceName',
  deliveryAddress: 'deliveryAddress',
  desiredDate: 'desiredDate',
  deceasedName: 'deceasedName',
  mournerName: 'mournerName',
  mortuaryRoom: 'mortuaryRoom',
  eventTarget: 'eventTarget',
  eventName: 'eventName',
  eventType: 'eventType',
}

/** 링크 결과에 폼에 채울 값이 하나라도 있나. 없으면 파싱은 '성공'이어도 사용자에겐 아무 일도 안 일어난 것이다. */
function hasLinkFields(parsed: ParsedLinkDto): boolean {
  return Object.entries(parsed.fields).some(([k, f]) => LINK_FIELD_MAP[k] != null && !!f?.value?.trim())
}

const LINK_EMPTY_MSG = '이 링크에서 주문 정보를 찾지 못했어요. 아래에서 직접 입력해 주세요.'

/** 링크 파서 자동 입력 고지 배지(BIZ-128). AI(LLM 폴백)면 그 사실을 명시한다. */
function AutoBadge({ via }: { via: 'parser' | 'ai' | undefined }) {
  if (!via) return null
  return <span className={s.autoBadge}>{via === 'ai' ? 'AI 자동 입력' : '자동 입력'}</span>
}

function Field({
  label,
  error,
  auto,
  children,
}: {
  label: string
  error?: string | undefined
  /** 링크 파서가 채운 필드 표시(BIZ-128) */
  auto?: 'parser' | 'ai' | undefined
  children: React.ReactNode
}) {
  // label이 입력을 감싸 암묵적으로 연결한다(htmlFor/id 없이 라벨 클릭·스크린리더 동작).
  return (
    <label className={s.group}>
      <span className={s.label}>
        {label}
        <AutoBadge via={auto} />
      </span>
      {children}
      {error && <p className="ferr">{error}</p>}
    </label>
  )
}
